import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaChevronDown, FaChevronRight, FaFutbol, FaBasketballBall, FaVolleyballBall,
  FaTimesCircle, FaSwimmingPool, FaMoneyBillWave, FaClock, FaCalendarAlt, FaInfoCircle,
  FaGraduationCap, FaMapMarkedAlt, FaUniversity, FaAddressCard,
  FaWallet, FaQuestionCircle, FaArrowLeft, FaCheckCircle, FaTrashAlt, FaPrint, FaExchangeAlt, FaFemale, FaSun
} from 'react-icons/fa';
import dayjs from 'dayjs';
import { GiTennisRacket } from 'react-icons/gi';
import api from '../../services/api';
import { verifySportsAppointment } from '../../services/sportsAppointmentService';
import RegionsManagement from '../SportsManagement/RegionsManagement';
import FacilitiesManagement from '../SportsManagement/FacilitiesManagement';
import PricingManagement from '../SportsManagement/PricingManagement';
import BlockedSlotsManagement from '../SportsManagement/BlockedSlotsManagement';
import { blockedSlotsService } from '../../services/sportsManagementService';
import { getAllClubApplications, updateClubApplicationStatus, deleteClubApplication, renewMembership, getClubTimeSlots, assignClubTimeSlot, removeClubTimeSlot, getClubPaymentInfo, getClubPaymentTransactions, transferSession, getClubDebts, addClubDebt, removeClubDebt, addClubSessionCancellation, removeClubSessionCancellation, addClubExtraSession, removeClubExtraSession } from '../../services/clubApplicationService';
import CardApplicationForm from './components/CardApplicationForm';
import CardApplicationsList from './components/CardApplicationsList';
import CardBalanceLoad from './components/CardBalanceLoad';
import CardTransactionsList from './components/CardTransactionsList';
import WomenTrainingApplicationsList from './components/WomenTrainingApplicationsList';
import PublicEducationManagement from './components/PublicEducationManagement';
import SummerSportsApplicationsList from './components/SummerSportsApplicationsList';
import TennisAcademyManagement from './components/TennisAcademyManagement';
import './SportsAppointments.scss';
import PremiumAppointmentDetailView from './PremiumAppointmentDetailView';
import { Modal, DatePicker } from 'antd'; // Add antd Modal
import PaymentDashboard from './components/PaymentDashboard';
import ReceiptPrint from '../../components/ReceiptPrint/ReceiptPrint';

// Base URL is handled by api instance


const facilityIcons = {
  Tenis: GiTennisRacket,
  Futbol: FaFutbol,
  Basketbol: FaBasketballBall,
  Voleybol: FaVolleyballBall,
  'Basketbol/Voleybol': FaBasketballBall,
  'Yüzme Havuzu': FaSwimmingPool,
  'Kapalı Spor Havuzu': FaSwimmingPool,
};

const getSlotOccupancyState = (slot, facility) => {
  let capacityValue = Number(slot?.capacity ?? slot?.Capacity ?? 1);
  const currentValue = Number(slot?.currentCount ?? slot?.CurrentCount ?? 0);

  if (facility) {
    const isPool = facility.type?.toLowerCase().includes('havuz') || facility.name?.toLowerCase().includes('havuz') || facility.Type?.toLowerCase().includes('havuz') || facility.Name?.toLowerCase().includes('havuz');
    if (!isPool) {
      capacityValue = 1;
    }
  }

  return {
    capacity: Number.isFinite(capacityValue) && capacityValue > 0 ? capacityValue : 1,
    currentCount: Number.isFinite(currentValue) && currentValue > 0 ? currentValue : 0
  };
};

const SportsAppointments = () => {
  const formRef = useRef(null);
  const navigate = useNavigate();

  // View State: 'appointments', 'regions', 'facilities', 'pricing', 'blocked-slots', 'cancellations', 'public-education', 'summer-sports', 'tennis-academy'
  const [activeView, setActiveView] = useState('appointments');
  const [appointmentTab, setAppointmentTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Get user role safely
  const userRole = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // App.jsx mantığıyla aynı fallback: rol yoksa admin varsay
      const r = user.userType || user.role || 'admin';
      return r.toString().toLowerCase();
    } catch {
      return 'admin';
    }
  }, []);

  const isSportsAdminRole = userRole === 'sports' || userRole === 'sporsuperadmin';
  const canManage = userRole === 'admin' || isSportsAdminRole || userRole === '99';
  const canManageMembershipHoliday = userRole === 'admin' || isSportsAdminRole;
  const canViewPaymentHistory = userRole === 'admin' || userRole === 'sporsuperadmin' || userRole === 'istirakler';
  const canCreateManualAppointment = userRole === 'admin';
  const canViewHoursManagement = userRole === 'admin';

  const [regions, setRegions] = useState([]);

  const isRegionalUser = userRole === 'regionalsports' || userRole === 'sorumlu';

  const hasPoolAccess = !isRegionalUser || regions.some(r =>
    (r.facilities || []).some(f => {
      const haystack = ((f.type || f.typeName || '') + ' ' + (f.name || f.facilityName || '')).toLowerCase();
      return haystack.includes('havuz') || haystack.includes('pool') || haystack.includes('yüzme') || haystack.includes('yuzme');
    })
  );
  const hasRaufAccess = !isRegionalUser || regions.some(r => (r.name || '').toLowerCase().includes('rauf'));
  const hasFitnessAccess = !isRegionalUser || regions.some(r =>
    (r.facilities || []).some(f =>
      ((f.type || f.typeName || '') + ' ' + (f.name || f.facilityName || '')).toLowerCase().includes('fitness')
    )
  );
  const canViewCardManagement = canManage || (isRegionalUser && (hasPoolAccess || hasFitnessAccess));

  const facilityRegionById = useMemo(() => {
    const map = new Map();
    (regions || []).forEach(region => {
      const regionName = region?.name || region?.regionName || '';
      (region?.facilities || []).forEach(facility => {
        const facilityId = Number(facility?.id ?? facility?.facilityId);
        if (!Number.isNaN(facilityId)) {
          map.set(facilityId, regionName);
        }
      });
    });
    return map;
  }, [regions]);

  const [expandedRegions, setExpandedRegions] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
    return today.toISOString().split('T')[0];
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedSportTypeId, setSelectedSportTypeId] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    email: '',
    phone: '',
    userGender: '',
    birthDate: ''
  });

  const [showPoolGuardianConsentModal, setShowPoolGuardianConsentModal] = useState(false);
  const [guardianConsentData, setGuardianConsentData] = useState({
    guardianFullName: '',
    guardianIdentityNumber: '',
    guardianBirthPlaceAndDate: '',
    isAccepted: false
  });

  const calculateParticipantAge = (birthDateString) => {
    if (!birthDateString) return null;
    const birthDate = new Date(birthDateString);
    if (isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [appointmentDetail, setAppointmentDetail] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [slotAppointments, setSlotAppointments] = useState([]);

  // Custom confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const [membershipHolidayForm, setMembershipHolidayForm] = useState({
    programType: 'Fitness',
    dayCount: '1',
    description: ''
  });
  const [membershipHolidayLoading, setMembershipHolidayLoading] = useState(false);
  const [downloadingHistory, setDownloadingHistory] = useState(false);
  const [downloadingClubHistory, setDownloadingClubHistory] = useState(false);
  
  // Receipt Print State
  const [receiptData, setReceiptData] = useState(null);

  const showToast = (message, type = 'success', duration = 4000) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => setToast(null), duration);
  };

  const submitMembershipHolidayExtension = async () => {
    if (!canManageMembershipHoliday) {
      showToast('Bu işlem için yetkiniz yok.', 'error');
      return;
    }

    const dayCount = Number(membershipHolidayForm.dayCount);
    if (!membershipHolidayForm.programType || !Number.isInteger(dayCount) || dayCount <= 0 || dayCount > 60) {
      showToast('Havuz/Fitness seçiniz ve 1-60 arasında gün sayısı giriniz.', 'error');
      return;
    }

    const programLabel = membershipHolidayForm.programType === 'Fitness' ? 'Fitness' : 'Havuz';
    Modal.confirm({
      title: 'Tatil Günü Ekle',
      content: `${programLabel} için şu an aktif olan üyeliklerin bitiş tarihine ${dayCount} gün eklenecek. Onaylıyor musunuz?`,
      okText: 'Onayla',
      cancelText: 'Vazgeç',
      onOk: async () => {
        setMembershipHolidayLoading(true);
        try {
          const response = await api.post('/api/card/membership-holiday-extension', {
            programType: membershipHolidayForm.programType,
            dayCount,
            description: membershipHolidayForm.description
          });

          if (response?.isSuccess) {
            showToast(response.resultMessage || 'Üyeliklere gün eklendi.', 'success');
            setMembershipHolidayForm((prev) => ({ ...prev, dayCount: '1', description: '' }));
          } else {
            showToast(response?.resultMessage || 'Üyeliklere gün eklenemedi.', 'error');
          }
        } catch (err) {
          showToast(err?.response?.data?.resultMessage || 'Üyeliklere gün eklenirken hata oluştu.', 'error');
        } finally {
          setMembershipHolidayLoading(false);
        }
      }
    });
  };

  const handleDownloadBlockingHistory = async (facilityId, facilityName) => {
    if (!facilityId) return;
    try {
      setDownloadingHistory(true);
      showToast('Kapatma geçmişi raporu hazırlanıyor...', 'info');
      const response = await blockedSlotsService.downloadBlockingHistoryExcel(facilityId);
      
      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const cleanName = (facilityName || 'Tesis').replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s]/g, '').trim().replace(/\s+/g, '_');
      link.setAttribute('download', `${cleanName}_Kapatma_Gecmisi.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast('Kapatma geçmişi başarıyla indirildi.', 'success');
    } catch (err) {
      console.error('Kapatma geçmişi indirme hatası:', err);
      showToast('Rapor indirilirken bir hata oluştu.', 'error');
    } finally {
      setDownloadingHistory(false);
    }
  };

  const handleDownloadClubAssignmentHistory = async (facilityId, facilityName) => {
    if (!facilityId) return;
    try {
      setDownloadingClubHistory(true);
      showToast('Kulüp saat atama geçmişi raporu hazırlanıyor...', 'info');
      const response = await blockedSlotsService.downloadClubAssignmentHistoryExcel(facilityId);
      
      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const cleanName = (facilityName || 'Tesis').replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s]/g, '').trim().replace(/\s+/g, '_');
      link.setAttribute('download', `${cleanName}_Kulup_Atama_Gecmisi.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast('Kulüp saat atama geçmişi başarıyla indirildi.', 'success');
    } catch (err) {
      console.error('Kulüp saat atama geçmişi indirme hatası:', err);
      showToast('Rapor indirilirken bir hata oluştu.', 'error');
    } finally {
      setDownloadingClubHistory(false);
    }
  };

  const canEditAppointments = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const role = (user.userType || user.role || 'admin').toString().toLowerCase();
      return role === 'admin' || role === 'sports' || role === 'sporsuperadmin' || role === '99';
    } catch {
      return true;
    }
  }, []);

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingAppointmentId, setPendingAppointmentId] = useState(null);
  const [verificationError, setVerificationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Club Applications State
  const [clubApplications, setClubApplications] = useState([]);
  const [clubApplicationsLoading, setClubApplicationsLoading] = useState(false);

  // Club Approval Modal
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalApp, setApprovalApp] = useState(null);
  const [approvalMembershipMonths, setApprovalMembershipMonths] = useState(3);

  // Club Time Slot Assignment Modal
  const [showSlotAssignModal, setShowSlotAssignModal] = useState(false);
  const [slotAssignApp, setSlotAssignApp] = useState(null);
  const [clubTimeSlots, setClubTimeSlots] = useState([]);
  const [slotAssignData, setSlotAssignData] = useState({ facilityId: '', dayOfWeek: 1, startTime: '08:00', endTime: '09:00', dateReference: 'today' });
  const [slotAssignLoading, setSlotAssignLoading] = useState(false);
  const [editSlotId, setEditSlotId] = useState(null);
  const [editSlotData, setEditSlotData] = useState({ facilityId: '', dayOfWeek: 1, startTime: '08:00', endTime: '09:00' });
  const [clubSessionActionModalVisible, setClubSessionActionModalVisible] = useState(false);
  const [clubSessionActionType, setClubSessionActionType] = useState('extra');
  const [clubSessionActionAssignment, setClubSessionActionAssignment] = useState(null);
  const [clubSessionActionDate, setClubSessionActionDate] = useState(null);
  const [clubSessionActionStartTime, setClubSessionActionStartTime] = useState('');
  const [clubSessionActionEndTime, setClubSessionActionEndTime] = useState('');
  const [clubSessionActionNotes, setClubSessionActionNotes] = useState('');
  const [clubSessionActionLoading, setClubSessionActionLoading] = useState(false);

  // Renewal Modal
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [renewalApp, setRenewalApp] = useState(null);
  const [renewalMonths, setRenewalMonths] = useState(3);

  // Club Status Filter: Varsayılan olarak onaylananlar ('approved') açılır
  const [clubStatusFilter, setClubStatusFilter] = useState('approved');
  const [clubFacilityFilter, setClubFacilityFilter] = useState('all');

  // Club Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalApp, setPaymentModalApp] = useState(null);
  const [paymentModalLoading, setPaymentModalLoading] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [paymentFacilityTab, setPaymentFacilityTab] = useState({});
  const [paymentTransactions, setPaymentTransactions] = useState([]);

  // Manual Payment State
  const [showManualPaymentForm, setShowManualPaymentForm] = useState(false);
  const [manualPaymentAmount, setManualPaymentAmount] = useState('');
  const [manualPaymentNotes, setManualPaymentNotes] = useState('');
  const [manualPaymentLoading, setManualPaymentLoading] = useState(false);

  // Transfer Modal State
  const [clubTransferModalVisible, setClubTransferModalVisible] = useState(false);
  const [clubTransferData, setClubTransferData] = useState(null);
  const [clubTransferDate, setClubTransferDate] = useState(null);
  const [clubTransferStartTime, setClubTransferStartTime] = useState('');
  const [clubTransferEndTime, setClubTransferEndTime] = useState('');
  const [clubTransferNotes, setClubTransferNotes] = useState('');
  const [clubTransferLoading, setClubTransferLoading] = useState(false);
  const [clubAvailableTransferSlots, setClubAvailableTransferSlots] = useState([]);
  const [clubAvailableTransferSlotsLoading, setClubAvailableTransferSlotsLoading] = useState(false);

  useEffect(() => {
    if (activeView === 'pricing' && !canViewHoursManagement) {
      setActiveView('appointments');
    }
  }, [activeView, canViewHoursManagement]);

  const clubFacilityOptions = useMemo(() => {
    const facilitiesById = new Map();

    (clubApplications || []).forEach(application => {
      const facilityId = Number(application?.facilityId ?? application?.sportsFacilityId);
      if (!Number.isNaN(facilityId) && facilityId > 0) {
        const key = String(facilityId);
        facilitiesById.set(key, {
          id: key,
          name: application?.facilityName || `Tesis ${facilityId}`
        });
      }
    });

    return Array.from(facilitiesById.values())
      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [clubApplications]);

  const filteredClubApplications = useMemo(() => {
    let list = Array.isArray(clubApplications) ? clubApplications : [];

    // Reddedilenleri ('rejected') sadece en üst admin (admin veya 99) görebilir
    if (userRole !== 'admin' && userRole !== '99') {
      list = list.filter(app => (app.status || '').toLowerCase() !== 'rejected');
    }

    // Status filter
    let statusFilteredList = list;
    if (clubStatusFilter === 'expired') {
      statusFilteredList = statusFilteredList.filter(app => app.isExpired || (app.status || '').toLowerCase() === 'expired');
    } else if (clubStatusFilter === 'approved') {
      statusFilteredList = statusFilteredList.filter(app => (app.status || '').toLowerCase() === 'approved' && !app.isExpired);
    } else if (clubStatusFilter && clubStatusFilter !== 'all') {
      statusFilteredList = statusFilteredList.filter(app => {
        const status = (app.status || '').toLowerCase();
        return status === clubStatusFilter.toLowerCase();
      });
    }

    if (clubFacilityFilter !== 'all') {
      const selectedFacility = clubFacilityOptions.find(option => option.id === clubFacilityFilter);
      const selectedFacilityName = (selectedFacility?.name || '').trim().toLowerCase();

      statusFilteredList = statusFilteredList.filter(app => {
        const appFacilityId = Number(app?.facilityId ?? app?.sportsFacilityId);
        if (!Number.isNaN(appFacilityId) && String(appFacilityId) === clubFacilityFilter) {
          return true;
        }

        const appFacilityName = (app?.facilityName || '').toString().trim().toLowerCase();
        return Boolean(selectedFacilityName && appFacilityName === selectedFacilityName);
      });
    }

    if (!isRegionalUser) return statusFilteredList;
    if (!Array.isArray(regions) || regions.length === 0) return statusFilteredList;

    const allowedRegionIds = new Set((regions || []).map(r => Number(r?.id ?? r?.regionId)).filter(id => !Number.isNaN(id)));
    const allowedRegionNames = new Set(
      (regions || []).map(r => (r?.name || r?.regionName || '').toString().trim().toLowerCase()).filter(Boolean)
    );
    const allowedFacilityIds = new Set();
    const allowedFacilityNames = new Set();
    (regions || []).forEach(region => {
      (region?.facilities || []).forEach(facility => {
        const facilityId = Number(facility?.id ?? facility?.facilityId);
        if (!Number.isNaN(facilityId)) {
          allowedFacilityIds.add(facilityId);
        }
        const facilityName = (facility?.name || facility?.facilityName || '').toString().trim().toLowerCase();
        if (facilityName) {
          allowedFacilityNames.add(facilityName);
        }
      });
    });

    const filtered = statusFilteredList.filter(app => {
      const appFacilityId = Number(app?.facilityId ?? app?.sportsFacilityId);
      if (!Number.isNaN(appFacilityId) && allowedFacilityIds.size > 0) {
        return allowedFacilityIds.has(appFacilityId);
      }

      const appRegionId = Number(app?.regionId ?? app?.sportsRegionId);
      if (!Number.isNaN(appRegionId) && allowedRegionIds.size > 0) {
        return allowedRegionIds.has(appRegionId);
      }

      const appRegionName = (app?.regionName || '').toString().trim().toLowerCase();
      if (appRegionName) {
        return allowedRegionNames.has(appRegionName);
      }

      const appFacilityName = (app?.facilityName || '').toString().trim().toLowerCase();
      if (appFacilityName) {
        if (allowedFacilityNames.has(appFacilityName)) return true;
        if (Array.from(allowedFacilityNames).some(name => appFacilityName.includes(name) || name.includes(appFacilityName))) {
          return true;
        }
        if (Array.from(allowedRegionNames).some(regionName => appFacilityName.includes(regionName))) {
          return true;
        }
      }

      return false;
    });

    // Backend zaten filtreli veri döndürebilir; frontend filtrelemesi yüzünden
    // yanlışlıkla tüm kayıtlar gizlenmesin.
    return filtered.length > 0 ? filtered : statusFilteredList;
  }, [clubApplications, userRole, regions, clubStatusFilter, clubFacilityFilter, clubFacilityOptions, isRegionalUser]);

  const getClubApplicationRegionName = (app) => {
    const regionFromField = (app?.regionName || '').toString().trim();
    if (regionFromField) return regionFromField;

    const regionFromId = facilityRegionById.get(Number(app?.facilityId ?? app?.sportsFacilityId));
    if (regionFromId) return regionFromId;

    const facilityName = (app?.facilityName || '').toString();
    if (facilityName.includes(' - ')) {
      return facilityName.split(' - ')[0].trim();
    }

    return '-';
  };

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' | 'oldest'
  const itemsPerPage = 15;

  // Transfer state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAppointment, setTransferAppointment] = useState(null);
  const [transferSlots, setTransferSlots] = useState([]);
  const [transferDate, setTransferDate] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  const isCreditAppointment = (appointment) =>
    String(appointment?.programType || '').toLowerCase() === 'fitness' ||
    Number(appointment?.creditsUsed || 0) > 0 ||
    Number(appointment?.cardMembershipId || 0) > 0;

  const isPaymentApproved = (a) => {
    if (isCreditAppointment(a)) return true;
    return (a.paymentStatus || '').toLowerCase() === 'approved';
  };

  const getAppointmentPaymentLabel = (appointment) => {
    if (isCreditAppointment(appointment)) {
      const usedCredits = Number(appointment?.creditsUsed || 1);
      return `${usedCredits} Kontör Kullanıldı`;
    }

    if (appointment?.paymentAmount) {
      return `${Number(appointment.paymentAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
    }

    return '0,00 TL';
  };

  const filteredAppointments = useMemo(() => {
    let filtered = [...appointments];

    // Fitness için 2 gün sonrası filtresi - "Alınan Randevular" listesinde gösterme
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    filtered = filtered.filter(a => {
      const isFitnessAppointment = String(a?.programType || '').toLowerCase() === 'fitness';
      if (!isFitnessAppointment) return true; // Fitness değilse göster
      
      const appointmentDate = new Date(a.appointmentDate);
      appointmentDate.setHours(0, 0, 0, 0);
      
      // Fitness randevuları için sadece bugün ve yarın olanları göster
      return appointmentDate <= tomorrow;
    });

    // Tab Filtering
    if (appointmentTab === 'confirmed') filtered = filtered.filter(a => isPaymentApproved(a));
    if (appointmentTab === 'paymentFailed') filtered = filtered.filter(a => !isPaymentApproved(a) && !['Cancelled', 'CancelledNoRefund', 'Refunded', 'RefundRequested'].includes(a.status));
    if (appointmentTab === 'cancelled') filtered = filtered.filter(a => ['Cancelled', 'CancelledNoRefund'].includes(a.status));
    if (appointmentTab === 'refunded') filtered = filtered.filter(a => ['Refunded', 'RefundRequested'].includes(a.status));

    // Search Filtering
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(a =>
        `${a.firstName} ${a.lastName} `.toLowerCase().includes(q) ||
        (a.identityNumber || '').includes(q)
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdDate || a.appointmentDate).getTime();
      const dateB = new Date(b.createdDate || b.appointmentDate).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [appointments, appointmentTab, searchQuery, sortOrder]);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [appointmentTab, searchQuery, sortOrder]);

  const paginatedAppointments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAppointments.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAppointments, currentPage]);

  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);

  const confirmedCount = appointments.filter(a => isPaymentApproved(a)).length;
  const paymentFailedCount = appointments.filter(a => !isPaymentApproved(a) && !['Cancelled', 'CancelledNoRefund', 'Refunded', 'RefundRequested'].includes(a.status)).length;
  const cancelledCount = appointments.filter(a => ['Cancelled', 'CancelledNoRefund'].includes(a.status)).length;
  const refundedCount = appointments.filter(a => ['Refunded', 'RefundRequested'].includes(a.status)).length;

  // Club Reservation State
  const [reservationType, setReservationType] = useState('Individual');
  const [personCount, setPersonCount] = useState(1);

  // ESC ile modal kapatma
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (showDetailModal) { setShowDetailModal(false); return; }
      if (showTransferModal) { setShowTransferModal(false); return; }
      if (showApprovalModal) { setShowApprovalModal(false); return; }
      if (showRenewalModal) { setShowRenewalModal(false); return; }
      if (showSlotAssignModal) { setShowSlotAssignModal(false); return; }
      if (clubSessionActionModalVisible) { setClubSessionActionModalVisible(false); return; }
      if (showVerificationModal) { setShowVerificationModal(false); return; }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showDetailModal, showTransferModal, showApprovalModal, showRenewalModal, showSlotAssignModal, clubSessionActionModalVisible, showVerificationModal]);

  useEffect(() => {
    if (!showSlotAssignModal) {
      setClubSessionActionModalVisible(false);
    }
  }, [showSlotAssignModal]);

  useEffect(() => {
    fetchRegionsAndFacilities();
    fetchAllAppointments();
  }, []);

  useEffect(() => {
    if (selectedFacility) {
      fetchAvailableTimeSlots(selectedFacility.id, selectedDate, selectedSportTypeId);
    }
  }, [selectedDate, selectedFacility?.id, selectedSportTypeId]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAllAppointments();
      if (selectedFacility) {
        fetchAvailableTimeSlots(selectedFacility.id, selectedDate, selectedSportTypeId);
      }
    }, 20000);
    return () => clearInterval(intervalId);
  }, [selectedFacility, selectedDate]);

  const fetchRegionsAndFacilities = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/sports/regions-with-facilities`);
      setRegions(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Bölgeler yüklenirken hata:', err);
      setError('Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAppointments = async () => {
    try {
      const response = await api.get(`/api/sports/appointments`);
      setAppointments(response.data || []);
    } catch (err) {
      console.error('Randevular yüklenirken hata:', err);
    }
  };

  const fetchClubApplications = async () => {
    try {
      setClubApplicationsLoading(true);
      const response = await getAllClubApplications();
      setClubApplications(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Kulüp başvuruları yüklenirken hata:', err);
    } finally {
      setClubApplicationsLoading(false);
    }
  };

  // Open Club Payment Modal
  const handleOpenPaymentModal = async (app) => {
    setPaymentModalApp(app);
    setShowPaymentModal(true);
    setPaymentModalLoading(true);
    setPaymentModalData(null);
    setPaymentFacilityTab({});
    setPaymentTransactions([]);
    setShowManualPaymentForm(false);
    setManualPaymentAmount('');
    setManualPaymentNotes('');
    try {
      // Önce getClubTimeSlots ile dene (Saat Ata ile aynı veri kaynağı)
      const slotsResponse = await getClubTimeSlots(app.id);
      const slots = slotsResponse?.data || [];

// import { ..., getClubDebts } from '../../services/clubApplicationService';
      // I'll assume getClubDebts is exported from clubApplicationService.js 

      // Borçları direkt TaxNumber ve FacilityId üzerinden çekiyoruz
      let additionalDebts = [];
      try {
          const debtsResponse = await getClubDebts(app.taxNumber || '00000000000', app.facilityId);
          if (debtsResponse?.isSuccess) {
              additionalDebts = debtsResponse.data || [];
          }
      } catch (err) {
          console.warn('Ek borçlar yüklenirken lokal hata:', err);
      }
      
      let paymentResponse = null;
      try {
          paymentResponse = await getClubPaymentInfo(app.taxNumber || '00000000000', {
            clubApplicationId: app.id,
            facilityId: app.facilityId
          });
      } catch (err) {
          console.warn('Ödeme özeti yüklenirken lokal hata (TaxNumber eksik olabilir):', err);
      }

      // Her halükarda eldeki "additionalDebts" dizimizi önceliklendiriyoruz.
      if (Array.isArray(slots) && slots.length > 0) {
        setPaymentModalData({
          source: 'timeSlots',
          clubName: `${app.firstName} ${app.lastName} `,
          taxNumber: app.taxNumber || '',
          membershipStartDate: app.membershipStartDate,
          membershipEndDate: app.membershipEndDate,
          assignments: slots,
          additionalDebts: additionalDebts 
        });
      } else {
        if (paymentResponse?.isSuccess && paymentResponse.data) {
          setPaymentModalData({ 
            source: 'paymentInfo', 
            ...paymentResponse.data,
            additionalDebts: additionalDebts.length > 0 ? additionalDebts : paymentResponse.data.additionalDebts || []
          });
        } else {
          // Both are empty/invalid, force the fallback even if additionalDebts behaves weirdly:
          if (additionalDebts.length > 0) {
            setPaymentModalData({
                source: 'timeSlots',
                clubName: `${app.firstName} ${app.lastName} `,
                taxNumber: app.taxNumber || '',
                assignments: [],
                additionalDebts: additionalDebts
            });
          } else {
            console.error("Ödeme Modal Hatası: slots 0, payment null, debts 0", { tax: app.taxNumber, fac: app.facilityId });
            setPaymentModalData({
                source: 'timeSlots',
                clubName: `${app.firstName} ${app.lastName} `,
                taxNumber: app.taxNumber || '',
                assignments: [],
                additionalDebts: []
            });
          }
        }
      }
    } catch (err) {
      console.error('Ödeme bilgisi yüklenirken hata:', err);
      setPaymentModalData(null);
    } finally {
      setPaymentModalLoading(false);
    }
    // Ödeme geçmişini çek (paralel)
    try {
      const txResponse = await getClubPaymentTransactions(app.id);
      const txData = txResponse?.data || [];
      setPaymentTransactions(Array.isArray(txData) ? txData : []);
    } catch (err) {
      console.error('Ödeme geçmişi yüklenirken hata:', err);
      setPaymentTransactions([]);
    }
  };

  // Fetch club applications when view changes to club-applications
  useEffect(() => {
    if (activeView === 'club-applications') {
      fetchClubApplications();
    }
  }, [activeView]);

  const handleSubmitManualPayment = async () => {
    const amount = parseFloat(manualPaymentAmount);
    if (isNaN(amount) || amount <= 0) {
      showToast('Lütfen geçerli bir tutar girin (0\'dan büyük).', 'error');
      return;
    }

    setManualPaymentLoading(true);
    try {
      const response = await addClubDebt({
        taxNumber: paymentModalApp.taxNumber,
        facilityId: paymentModalApp.facilityId,
        amount: parseFloat(manualPaymentAmount),
        notes: manualPaymentNotes
      });

      if (response?.isSuccess) {
        showToast('Ek borçlandırma başarıyla kaydedildi.', 'success');
        setShowManualPaymentForm(false);
        setManualPaymentAmount('');
        setManualPaymentNotes('');
        // Refresh payment modal to show new data
        handleOpenPaymentModal(paymentModalApp);
      } else {
        showToast(response?.resultMessage || 'Ek borç eklenirken bir hata oluştu.', 'error');
      }
    } catch (error) {
      console.error('Ek borç ekleme hatası:', error);
      showToast('Borç eklenirken sistem hatası oluştu.', 'error');
    } finally {
      setManualPaymentLoading(false);
    }
  };

  const handleRemoveClubDebt = async (debtId) => {
    if (!window.confirm('Bu ek borçlandırmayı silmek istediğinize emin misiniz?')) return;
    
    try {
      const response = await removeClubDebt(debtId);
      if (response?.isSuccess) {
        showToast('Ek borç başarıyla silindi.', 'success');
        handleOpenPaymentModal(paymentModalApp); // refresh modal data
      } else {
        showToast(response?.message || response?.resultMessage || 'Silinirken bir hata oluştu.', 'error');
      }
    } catch (error) {
      console.error('Ek borç silme hatası:', error);
      showToast('Sistem hatası.', 'error');
    }
  };


  // ── Club Approval Handlers ──
  const handleOpenApprovalModal = (app) => {
    setApprovalApp(app);
    setApprovalMembershipMonths(3);
    setShowApprovalModal(true);
  };

  const handleApproveMembership = async (appId, months) => {
    if (!appId) return;

    Modal.confirm({
      title: 'Kulüp Onayı',
      content: `${months} aylık kulüp üyeliğini onaylamak istediğinize emin misiniz ? `,
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: async () => {
        try {
          const response = await updateClubApplicationStatus(
            appId,
            'Approved',
            '',
            months
          );
          if (response?.isSuccess === false) {
            showToast(response?.resultMessage || 'Onay sırasında hata oluştu.', 'error');
            return;
          }
          showToast(`Kulüp onaylandı(${months} ay üyelik).`);
          setShowApprovalModal(false);
          fetchClubApplications();
        } catch (err) {
          console.error('Onay hatası:', err);
          showToast('Onay sırasında hata oluştu.', 'error');
        }
      }
    });
  };

  const handleRejectClubApplication = async (application) => {
    if (!application) return;

    Modal.confirm({
      title: 'Red Onayı',
      content: 'Bu başvuruyu reddetmek istediğinize emin misiniz?',
      okText: 'Evet, Reddet',
      cancelText: 'Vazgeç',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await updateClubApplicationStatus(application.id, 'Rejected');
          if (response?.isSuccess === false) {
            showToast(response?.resultMessage || 'Başvuru reddedilemedi.', 'error');
            return;
          }

          showToast('Başvuru reddedildi.', 'success');
          fetchClubApplications();
        } catch (err) {
          console.error('Red hatası:', err);
          showToast('Başvuru reddedilirken hata oluştu.', 'error');
        }
      }
    });
  };

  const handleOpenRenewalModal = (app) => {
    setRenewalApp(app);
    setRenewalMonths(3);
    setShowRenewalModal(true);
  };

  const handleRenewMembership = async () => {
    if (!renewalApp) return;

    Modal.confirm({
      title: 'Yenileme Onayı',
      content: `Üyeliği ${renewalMonths} ay daha uzatmak istediğinize emin misiniz?

⚠️ Dikkat: Geçmiş üyelik tarihindeki saatler ödemeye kapanacaktır. Yeni üyelik dönemi bugünden itibaren başlayacaktır.`,
      okText: 'Evet, Yenile',
      cancelText: 'Hayır',
      onOk: async () => {
        try {
          await renewMembership(renewalApp.id, renewalMonths);
          showToast(`Üyelik süresi ${renewalMonths} ay olarak yenilendi.`);
          setShowRenewalModal(false);
          fetchClubApplications();
        } catch (err) {
          console.error('Yenileme hatası:', err);
          showToast('Yenileme sırasında hata oluştu.', 'error');
        }
      }
    });
  };

  // ── Time Slot Assignment Handlers ──
  const handleOpenSlotAssignModal = async (app) => {
    setSlotAssignApp(app);
    const resolvedFacilityId = app.facilityId || app.FacilityId || app.sportsFacilityId || '';
    setSlotAssignData({ facilityId: resolvedFacilityId, dayOfWeek: 1, startTime: '08:00', endTime: '09:00', dateReference: 'today' });
    setShowSlotAssignModal(true);
    try {
      const res = await getClubTimeSlots(app.id);
      setClubTimeSlots(res.data?.data || res.data || []);
    } catch (e) {
      console.error(e);
      setClubTimeSlots([]);
    }
  };

  const refreshClubTimeSlotAssignments = async (clubAppId) => {
    if (!clubAppId) {
      setClubTimeSlots([]);
      return;
    }

    const response = await getClubTimeSlots(clubAppId);
    setClubTimeSlots(response.data?.data || response.data || []);
  };

  const openClubSessionActionModal = (slot, type) => {
    setClubSessionActionType(type);
    setClubSessionActionAssignment(slot);
    setClubSessionActionDate(null);
    setClubSessionActionStartTime(type === 'extra' ? slot.startTime : '');
    setClubSessionActionEndTime(type === 'extra' ? slot.endTime : '');
    setClubSessionActionNotes('');
    setClubSessionActionModalVisible(true);
  };

  const handleSubmitClubSessionAction = async () => {
    if (!clubSessionActionAssignment || !clubSessionActionDate) {
      showToast('Lütfen tarih seçin.', 'error');
      return;
    }

    if (clubSessionActionType === 'extra') {
      if (!clubSessionActionStartTime || !clubSessionActionEndTime) {
        showToast('Lütfen başlangıç ve bitiş saatini seçin.', 'error');
        return;
      }
      const [startH, startM] = clubSessionActionStartTime.split(':').map(Number);
      const [endH, endM] = clubSessionActionEndTime.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      let endMinutes = endH * 60 + endM;
      if (endH === 0 && endM === 0) {
        endMinutes = 24 * 60;
      }
      const durationMinutes = endMinutes - startMinutes;
      if (durationMinutes <= 0) {
        showToast('Bitiş saati, başlangıç saatinden sonra olmalıdır.', 'error');
        return;
      }
    }

    setClubSessionActionLoading(true);
    try {
      const payloadDate = dayjs(clubSessionActionDate).format('YYYY-MM-DD');

      if (clubSessionActionType === 'remove') {
        const response = await addClubSessionCancellation({
          assignmentId: clubSessionActionAssignment.assignmentId,
          sessionDate: payloadDate,
          reason: clubSessionActionNotes || null
        });

        if (response?.isSuccess === false) {
          showToast(response?.resultMessage || 'Tekil çıkarma kaydedilemedi.', 'error');
          return;
        }

        showToast(response?.resultMessage || 'Tekil tarih başarıyla çıkarıldı.', 'success');
      } else {
        const response = await addClubExtraSession({
          assignmentId: clubSessionActionAssignment.assignmentId,
          sessionDate: payloadDate,
          startTime: clubSessionActionStartTime,
          endTime: clubSessionActionEndTime,
          notes: clubSessionActionNotes || null
        });

        if (response?.isSuccess === false) {
          showToast(response?.resultMessage || 'Ek seans kaydedilemedi.', 'error');
          return;
        }

        showToast(response?.resultMessage || 'Ek seans başarıyla eklendi.', 'success');
      }

      setClubSessionActionModalVisible(false);
      await refreshClubTimeSlotAssignments(slotAssignApp?.id);
      if (selectedFacility?.id) {
        await fetchAvailableTimeSlots(selectedFacility.id, selectedDate, selectedSportTypeId);
      }
      await fetchAllAppointments();
    } catch (err) {
      console.error('Kulup tekil seans islem hatasi:', err);
      showToast(err?.response?.data?.resultMessage || 'İşlem sırasında hata oluştu.', 'error');
    } finally {
      setClubSessionActionLoading(false);
    }
  };

  const handleDeleteClubSessionAction = async (type, id) => {
    Modal.confirm({
      title: type === 'extra' ? 'Ek seansı kaldır' : 'Tekil çıkarma kaydını kaldır',
      content: type === 'extra'
        ? 'Bu ek seans kaydını kaldırmak istediğinize emin misiniz?'
        : 'Bu tekil çıkarma kaydını kaldırmak istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'İptal',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = type === 'extra'
            ? await removeClubExtraSession(id)
            : await removeClubSessionCancellation(id);

          if (response?.isSuccess === false) {
            showToast(response?.resultMessage || 'Kayıt kaldırılamadı.', 'error');
            return;
          }

          showToast(response?.resultMessage || 'Kayıt kaldırıldı.', 'success');
          await refreshClubTimeSlotAssignments(slotAssignApp?.id);
          if (selectedFacility?.id) {
            await fetchAvailableTimeSlots(selectedFacility.id, selectedDate, selectedSportTypeId);
          }
          await fetchAllAppointments();
        } catch (err) {
          console.error('Kulup tekil seans silme hatasi:', err);
          showToast(err?.response?.data?.resultMessage || 'Kayıt kaldırılırken hata oluştu.', 'error');
        }
      }
    });
  };

  const handleRejectApplication = (application) => {
    if (!application) return;

    Modal.confirm({
      title: 'Red Onayı',
      content: 'Bu başvuruyu reddetmek istediğinize emin misiniz?',
      okText: 'Evet, Reddet',
      cancelText: 'Vazgeç',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await updateClubApplicationStatus(application.id, 'Rejected');
          if (response?.isSuccess === false) {
            showToast(response?.resultMessage || 'Başvuru reddedilemedi.', 'error');
            return;
          }

          showToast('Başvuru reddedildi.', 'success');
          fetchClubApplications();
        } catch (err) {
          console.error('Red hatası:', err);
          showToast('Başvuru reddedilirken hata oluştu.', 'error');
        }
      }
    });
  };

  const handleAssignTimeSlot = async () => {
    const facilityId = Number(slotAssignData.facilityId || slotAssignApp?.facilityId || slotAssignApp?.FacilityId || slotAssignApp?.sportsFacilityId || 0);

    if (!slotAssignApp || facilityId <= 0 || isNaN(facilityId)) {
      showToast('Tesis bilgisi bulunamadı. Lütfen listeden bir tesis seçin.', 'error');
      return;
    }

    const [startH, startM] = slotAssignData.startTime.split(':').map(Number);
    const [endH, endM] = slotAssignData.endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;
    if (endH === 0 && endM === 0) {
      endMinutes = 24 * 60;
    }
    const durationMinutes = endMinutes - startMinutes;

    if (durationMinutes <= 0) {
      showToast('Bitiş saati, başlangıç saatinden sonra olmalıdır.', 'error');
      return;
    }
    if (durationMinutes > 60) {
      showToast('Saat ataması en fazla 1 saat olabilir.', 'error');
      return;
    }

    const dayNum = Number(slotAssignData.dayOfWeek);
    const sameDaySlots = clubTimeSlots.filter(s => Number(s.dayOfWeek) === dayNum && s.isActive !== false);
    const hasOverlap = sameDaySlots.some(existing => {
      const [exStartH, exStartM] = existing.startTime.split(':').map(Number);
      const [exEndH, exEndM] = existing.endTime.split(':').map(Number);
      const exStart = exStartH * 60 + (exStartM || 0);
      let exEnd = exEndH * 60 + (exEndM || 0);
      if (exEndH === 0 && exEndM === 0) {
        exEnd = 24 * 60;
      }
      return startMinutes < exEnd && endMinutes > exStart;
    });

    if (hasOverlap) {
      showToast('Bu gün ve saat aralığında zaten bir atama mevcut! Çakışan saatler atanamaz.', 'error');
      return;
    }

    Modal.confirm({
      title: 'Saat Atama Onayı',
      content: slotAssignData.dateReference === 'today'
        ? 'Bu saate atama yapmak istediğinize emin misiniz? Seanslar bugünden itibaren hesaplanacaktır.'
        : 'Bu saate atama yapmak istediğinize emin misiniz? Seanslar üyelik başlangıç tarihinden itibaren hesaplanacaktır.',
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: async () => {
        setSlotAssignLoading(true);
        try {
          const res = await assignClubTimeSlot(slotAssignApp.id, {
            facilityId: Number(facilityId),
            dayOfWeek: Number(slotAssignData.dayOfWeek),
            startTime: slotAssignData.startTime,
            endTime: slotAssignData.endTime,
            dateReference: slotAssignData.dateReference || 'today'
          });
          const apiRes = res?.isSuccess !== undefined ? res : (res?.data || res);
          if (apiRes && apiRes.isSuccess === false) {
            showToast(apiRes.resultMessage || 'Atama yapılamadı.', 'error');
          } else if (apiRes && apiRes.isSuccess === true) {
            showToast(apiRes.resultMessage || 'Saat ataması başarıyla yapıldı.', 'success');
            const slotsRes = await getClubTimeSlots(slotAssignApp.id);
            const freshSlots = Array.isArray(slotsRes) ? slotsRes : (slotsRes?.data?.data || slotsRes?.data || []);
            setClubTimeSlots(freshSlots);
          } else {
            showToast(apiRes?.resultMessage || apiRes?.message || 'İşlem gerçekleştirilemedi.', 'error');
          }
        } catch (err) {
          console.error('Atama hatası:', err);
          const errorMsg = err?.resultMessage || err?.response?.data?.resultMessage || err?.response?.data?.message || err?.message || 'Atama sırasında hata oluştu.';
          showToast(errorMsg, 'error');
        } finally {
          setSlotAssignLoading(false);
        }
      }
    });
  };

  const handleRemoveTimeSlot = async (assignmentId) => {
    Modal.confirm({
      title: 'Saat Atamasını Kaldır',
      content: 'Bu atamayı kaldırmak istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      okType: 'danger',
      onOk: async () => {
        try {
          await removeClubTimeSlot(assignmentId);
          showToast('Saat ataması kaldırıldı.');
          if (slotAssignApp) {
            const res = await getClubTimeSlots(slotAssignApp.id);
            setClubTimeSlots(res.data?.data || res.data || []);
          }
        } catch (err) {
          console.error('Silme hatası:', err);
          showToast('Silinirken hata oluştu.', 'error');
        }
      }
    });
  };

  const fetchAvailableTimeSlots = async (facilityId, date, sportTypeId = null) => {
    try {
      const params = { date };
      if (sportTypeId) params.sportTypeId = sportTypeId;
      const response = await api.get(
        `/api/sports/available-slots/${facilityId}`,
        { params }
      );

      setSelectedFacility(prev => ({
        ...prev,
        timeSlots: response.data || []
      }));
    } catch (err) {
      console.error('Saat dilimleri yüklenirken hata:', err);
    }
  };

  const fetchTransferSlots = async (facilityId, date) => {
    try {
      setTransferLoading(true);
      const response = await api.get(`/api/sports/available-slots/${facilityId}`, { params: { date } });
      setTransferSlots(response.data || response || []);
    } catch (err) {
      console.error('Transfer slotları yüklenirken hata:', err);
      setTransferSlots([]);
    } finally {
      setTransferLoading(false);
    }
  };

  const handleCreateAppointment = async (bypassConsentModal = false) => {
    if (!canCreateManualAppointment) {
      showToast('Manuel randevu oluşturma yetkiniz bulunmamaktadır.', 'error');
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.idNumber || !formData.email || !formData.phone || !formData.userGender) {
      showToast('Lütfen tüm zorunlu alanları doldurunuz.', 'error');
      return;
    }

    if (formData.idNumber.replace(/\D/g, '').length !== 11) {
      showToast('TC Kimlik numarası 11 haneli olmalıdır.', 'error');
      return;
    }

    if (formData.phone.replace(/\D/g, '').length !== 10) {
      showToast('Telefon numarası 10 haneli olmalıdır. (5XXXXXXXXX)', 'error');
      return;
    }

    if (!selectedTimeSlot) {
      showToast('Lütfen bir saat dilimi seçin.', 'error');
      return;
    }

    if (!selectedTimeSlot.available && selectedTimeSlot.blockSource === 'Club') {
      showToast('Bu saat bir kulübe ayrılmıştır, randevu alınamaz.', 'error');
      return;
    }

    const facilityTypeName = (selectedFacility?.typeName || selectedFacility?.name || selectedFacility?.type || '').toLowerCase();
    const isFitness = facilityTypeName.includes('fitness');
    const isPool = facilityTypeName.includes('havuz') || facilityTypeName.includes('pool') || facilityTypeName.includes('yüzme');

    const participantAge = calculateParticipantAge(formData.birthDate);

    // Fitness 16 yaş sınırı kontrolü
    if (isFitness && participantAge !== null && participantAge < 16) {
      showToast('Fitness randevusu alabilmek için en az 16 yaşında olunmalıdır.', 'error');
      return;
    }

    // Havuz 6-18 yaş (18 yaşından küçük) veli izin formu kontrolü
    if (isPool && participantAge !== null && participantAge >= 6 && participantAge < 18 && !bypassConsentModal) {
      if (!guardianConsentData.isAccepted || !guardianConsentData.guardianFullName || !guardianConsentData.guardianIdentityNumber) {
        setShowPoolGuardianConsentModal(true);
        showToast('6-18 yaş arasındaki havuz randevuları için Veli İzin Formu ve Sağlık Beyanı doldurulup onaylanmalıdır.', 'warning');
        return;
      }
    }

    try {
      const appointmentData = {
        facilityId: selectedFacility.id,
        appointmentDate: selectedDate,
        startTime: selectedTimeSlot.time.split(' - ')[0],
        endTime: selectedTimeSlot.time.split(' - ')[1],
        firstName: formData.firstName,
        lastName: formData.lastName,
        identityNumber: formData.idNumber,
        email: formData.email,
        phone: formData.phone,
        userGender: formData.userGender,
        createdByWeb: false,
        sportTypeId: selectedSportTypeId,
        reservationType: reservationType,
        personCount: personCount,
        participantBirthDate: formData.birthDate || null,
        guardianFullName: guardianConsentData.guardianFullName || null,
        guardianIdentityNumber: guardianConsentData.guardianIdentityNumber || null,
        guardianBirthPlaceAndDate: guardianConsentData.guardianBirthPlaceAndDate || null,
        isGuardianConsentAccepted: guardianConsentData.isAccepted || false
      };

      await api.post(`/api/sports/appointments`, appointmentData);

      showToast(`Randevu başarıyla oluşturuldu!\nAd Soyad: ${formData.firstName} ${formData.lastName}\nSaha: ${selectedFacility.name}\nTarih: ${new Date(selectedDate).toLocaleDateString('tr-TR')}\nSaat: ${selectedTimeSlot.time}`, 'success', 6000);

      setFormData({ firstName: '', lastName: '', idNumber: '', email: '', phone: '', userGender: '', birthDate: '' });
      setGuardianConsentData({ guardianFullName: '', guardianIdentityNumber: '', guardianBirthPlaceAndDate: '', isAccepted: false });
      setShowPoolGuardianConsentModal(false);
      setReservationType('Individual');
      setPersonCount(1);
      setSelectedTimeSlot(null);
      fetchAvailableTimeSlots(selectedFacility.id, selectedDate, selectedSportTypeId);
      fetchAllAppointments();
    } catch (err) {
      console.error('Randevu oluşturulurken hata:', err);
      const serverMsg = err?.response?.data?.resultMessage || err?.resultMessage;
      showToast(serverMsg || 'Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.', 'error');
    }
  };

  const getDayName = (dayNum) => {
    const days = { 0: 'Pazar', 1: 'Pazartesi', 2: 'Salı', 3: 'Çarşamba', 4: 'Perşembe', 5: 'Cuma', 6: 'Cumartesi' };
    return days[dayNum] || dayNum;
  };

  const handleTransferClick = (appointment) => {
    setTransferAppointment(appointment);
    // Default: same day as appointment
    const appointmentDateStr = new Date(appointment.appointmentDate).toISOString().split('T')[0];
    setTransferDate(appointmentDateStr);
    setShowTransferModal(true);
    fetchTransferSlots(appointment.facilityId, appointmentDateStr);
  };

  const executeTransfer = async (targetDate, targetStartTime, targetEndTime) => {
    if (!transferAppointment?.id) return;

    try {
      setActionLoadingId(transferAppointment.id);
      const response = await api.post(`/api/sports/appointments/${transferAppointment.id}/transfer`, {
        newDate: targetDate,
        newStartTime: targetStartTime,
        newEndTime: targetEndTime
      });
      showToast(response?.resultMessage || 'Randevu başarıyla aktarıldı.', 'success');
      setShowTransferModal(false);
      setTransferAppointment(null);
      await fetchAllAppointments();
      if (selectedFacility) {
        await fetchAvailableTimeSlots(selectedFacility.id, selectedDate, selectedSportTypeId);
      }
    } catch (err) {
      const message = err?.response?.data?.resultMessage || err?.resultMessage || 'Randevu aktarılamadı.';
      showToast(message, 'error');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleTransferAppointment = (targetDate, targetStartTime, targetEndTime) => {
    if (!transferAppointment?.id) return;

    // Geçmiş tarihe aktarımı engelle
    const now = new Date();
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (target < today) {
      showToast('Geçmiş tarihe randevu aktarılamaz.', 'error');
      return;
    }

    // Bugünse, geçmiş saate aktarımı engelle
    if (target.getTime() === today.getTime()) {
      const [startHour, startMin] = targetStartTime.split(':').map(Number);
      const slotTime = new Date();
      slotTime.setHours(startHour, startMin, 0, 0);
      if (slotTime <= now) {
        showToast('Geçmiş saate randevu aktarılamaz.', 'error');
        return;
      }
    }

    setConfirmDialog({
      message: `Randevuyu ${new Date(targetDate).toLocaleDateString('tr-TR')} tarihine, ${targetStartTime} - ${targetEndTime} saatine aktarmak istediğinize emin misiniz?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        await executeTransfer(targetDate, targetStartTime, targetEndTime);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleTransferDateChange = (newDate) => {
    setTransferDate(newDate);
    if (transferAppointment) {
      fetchTransferSlots(transferAppointment.facilityId, newDate);
    }
  };

  const handleNextDay = () => {
    const current = new Date(transferDate);
    current.setDate(current.getDate() + 1);
    const nextDateStr = current.toISOString().split('T')[0];
    handleTransferDateChange(nextDateStr);
  };

  const handlePrevDay = () => {
    const current = new Date(transferDate);
    current.setDate(current.getDate() - 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (current >= today) {
      const prevDateStr = current.toISOString().split('T')[0];
      handleTransferDateChange(prevDateStr);
    }
  };

  const toggleRegion = (regionId) => {
    setExpandedRegions(prev => {
      if (prev.includes(regionId)) {
        return prev.filter(id => id !== regionId);
      } else {
        return [...prev, regionId];
      }
    });
  };

  const handleFacilityClick = async (facility, region) => {
    setSelectedFacility({
      ...facility,
      regionName: region.name,
      timeSlots: []
    });
    setSelectedTimeSlot(null);

    await fetchAvailableTimeSlots(facility.id, selectedDate, facility.isSharedFacility ? facility.availableSports?.[0]?.typeId : null);

    console.log('Selected Facility:', facility);
    console.log('Allow Club:', facility.allowClubReservations);

    if (facility.isSharedFacility && facility.availableSports?.length > 0) {
      setSelectedSportTypeId(facility.availableSports[0].typeId);
    } else {
      setSelectedSportTypeId(null);
    }
    setReservationType('Individual');
    setPersonCount(1);
  };

  const handleCapacitySlotClick = async (slot) => {
    const startTime = slot.time.split(' - ')[0];
    const endTime = slot.time.split(' - ')[1];

    try {
      setShowDetailModal(false);
      setAppointmentDetail(null);
      setSelectedTimeSlot(slot);
      setSlotAppointments([]);

      // Fitness tesisi için 2 günden sonrasındaki randevu detaylarını gösterme
      const isFitnessFacility = String(selectedFacility?.type || '').toLowerCase().includes('fitness');
      if (isFitnessFacility) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const slotDate = new Date(selectedDate);
        slotDate.setHours(0, 0, 0, 0);

        if (slotDate > tomorrow) {
          // 2 günden sonrası - randevu detaylarını gösterme
          setTimeout(() => {
            formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
          return;
        }
      }

      const response = await api.get(
        `/api/sports/slot-appointments/${selectedFacility.id}`,
        { params: { date: selectedDate, startTime, endTime } }
      );
      setSlotAppointments(response.data || response || []);

      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (err) {
      console.error('Kapasiteli slot randevulari yuklenirken hata:', err);
      setSlotAppointments([]);
    }
  };

  const handleTimeSlotClick = async (slot) => {
    const occupancy = getSlotOccupancyState(slot, selectedFacility);
    const isFitnessFacility = String(selectedFacility?.type || '').toLowerCase().includes('fitness');
    const blockSource = String(slot.blockSource || '');
    const isClubSlot = !slot.available && blockSource === 'Club';
    const isManagementBlocked = !slot.available && (
      blockSource === 'Maintenance' ||
      blockSource === 'PublicEducation' ||
      blockSource === 'PublicEducationCourse'
    );
    // For shared-capacity slots, always prefer the participant list whenever the slot has occupancy.
    // Some fitness full slots arrive with misleading status values although they are simply full.
    const shouldShowCapacitySummary = occupancy.capacity > 1 && occupancy.currentCount > 0 && !isClubSlot;
    const isCapacitySlot = occupancy.capacity > 1 && !isClubSlot && !isManagementBlocked;
    const startTime = slot.time.split(' - ')[0];
    const endTime = slot.time.split(' - ')[1];

    if (isFitnessFacility && occupancy.capacity > 1 && !isClubSlot && !isManagementBlocked) {
      await handleCapacitySlotClick(slot);
      return;
    }

    if (shouldShowCapacitySummary && !isManagementBlocked) {
      await handleCapacitySlotClick(slot);
      return;
    }

    const normalizeTime = (raw) => {
      if (!raw) return '';
      const value = String(raw).trim();
      const match = value.match(/^(\d{1,2}):(\d{2})/);
      if (!match) return value;
      return `${match[1].padStart(2, '0')}:${match[2]}`;
    };

    const loadSlotAppointments = async () => {
      try {
        // Fitness tesisi için 2 günden sonrasındaki randevu detaylarını gösterme
        const isFitnessFacility = String(selectedFacility?.type || '').toLowerCase().includes('fitness');
        if (isFitnessFacility) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(today.getDate() + 1);
          const slotDate = new Date(selectedDate);
          slotDate.setHours(0, 0, 0, 0);
          if (slotDate > tomorrow) {
            setSlotAppointments([]);
            return;
          }
        }

        if (shouldShowCapacitySummary) {
          const response = await api.get('/api/sports/appointments', {
            params: { facilityId: selectedFacility.id, date: selectedDate }
          });

          const allAppointments = response?.data || [];
          const filteredAppointments = allAppointments.filter((appt) => {
            const appointmentStart = normalizeTime(appt.startTime);
            const appointmentEnd = normalizeTime(appt.endTime);
            const selectedStart = normalizeTime(startTime);
            const selectedEnd = normalizeTime(endTime);

            return appointmentStart === selectedStart &&
              (!selectedEnd || !appointmentEnd || appointmentEnd === selectedEnd || appointmentEnd === '00:00');
          });

          setSlotAppointments(filteredAppointments);
          return;
        }

        const response = await api.get(
          `/api/sports/slot-appointments/${selectedFacility.id}`,
          { params: { date: selectedDate, startTime, endTime } }
        );
        setSlotAppointments(response.data || response || []);
      } catch (err) {
        console.error('Slot randevulari yuklenirken hata:', err);
        setSlotAppointments([]);
      }
    };

    if (slot.available) {
      setSelectedTimeSlot(slot);
      await loadSlotAppointments();
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);

      if (shouldShowCapacitySummary) {
        setShowDetailModal(false);
        setAppointmentDetail(null);
        return;
      }

      if (false && shouldShowCapacitySummary) {
        setShowDetailModal(true);
        setAppointmentDetail({
          id: 0,
          facilityName: selectedFacility?.name || '',
          regionName: selectedFacility?.regionName || '',
          appointmentDate: selectedDate,
          startTime: slot.time.split(' - ')[0],
          endTime: slot.time.split(' - ')[1],
          reservationType: 'CapacitySummary',
          status: 'Available',
          blockSource: null,
          sportTypeName: slot.allowedGender === 'Female' ? 'Kadın Saati' : (slot.allowedGender === 'Male' ? 'Erkek Saati' : 'Kapasite Slotu'),
          currentCount: occupancy.currentCount,
          capacity: occupancy.capacity
        });

        await loadSlotAppointments();
      }
    } else {
      if (shouldShowCapacitySummary) {
        setSelectedTimeSlot(slot);
        setShowDetailModal(false);
        setAppointmentDetail(null);
        setSlotAppointments([]);
        await loadSlotAppointments();
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return;
      }

      // Optimistic UI: Show modal immediately with loading state
      setShowDetailModal(true);
      setAppointmentDetail(null); // Clear previous detail
      setSlotAppointments([]);

      if (false && shouldShowCapacitySummary) {
        setAppointmentDetail({
          id: 0,
          facilityName: selectedFacility?.name || '',
          regionName: selectedFacility?.regionName || '',
          appointmentDate: selectedDate,
          startTime: slot.time.split(' - ')[0],
          endTime: slot.time.split(' - ')[1],
          reservationType: 'CapacitySummary',
          status: 'Full',
          blockSource: null,
          sportTypeName: slot.allowedGender === 'Female' ? 'Kadın Saati' : (slot.allowedGender === 'Male' ? 'Erkek Saati' : 'Kapasite Slotu'),
          currentCount: occupancy.currentCount,
          capacity: occupancy.capacity
        });
        // Fetch actual appointments for this slot
        await loadSlotAppointments();
        return;
      }

      try {
        if (isFitnessFacility && !isClubSlot && !isManagementBlocked) {
          setShowDetailModal(false);
          await handleCapacitySlotClick(slot);
          return;
        }

        const response = await api.get(
          `/api/sports/appointment-details/${selectedFacility.id}`,
          { params: { date: selectedDate, startTime } }
        );
        if (response?.data) {
          setAppointmentDetail(response.data);
        } else {
          // Fallback: slot bilgisinden detay oluştur
          setAppointmentDetail({
            firstName: 'Bilinmiyor',
            lastName: slot.blockReason || 'Dolu',
            facilityName: selectedFacility?.name || '',
            regionName: selectedFacility?.regionName || '',
            startTime: slot.time.split(' - ')[0],
            endTime: slot.time.split(' - ')[1],
            blockSource: slot.blockSource || 'Maintenance',
            reservationType: 'Blocked',
            sportTypeName: slot.blockReason || 'Belirtilmemiş',
            status: 'Blocked'
          });
        }
      } catch (err) {
        console.error('Randevu detayları yüklenirken hata:', err);
        showToast('Randevu detayları yüklenirken hata oluştu.', 'error');
        setShowDetailModal(false);
      }
    }
  };

  const openTransferModal = (session) => {
    setClubTransferData({
      assignmentId: session.assignmentId,
      sessionDate: session.sessionDate,
      dayOfWeek: session.dayOfWeek,
      startTime: session.startTime,
      endTime: session.endTime,
      facilityId: session.facilityId
    });
    setClubTransferDate(null);
    setClubTransferStartTime('');
    setClubTransferEndTime('');
    setClubTransferNotes('');
    setClubAvailableTransferSlots([]);
    setClubTransferModalVisible(true);
  };

  const selectedTimeSlotOccupancy = selectedTimeSlot
    ? getSlotOccupancyState(selectedTimeSlot, selectedFacility)
    : { capacity: 1, currentCount: 0 };
  const shouldSuppressDetailModalForSelectedSlot =
    !!selectedTimeSlot &&
    selectedTimeSlotOccupancy.capacity > 1 &&
    String(selectedTimeSlot.blockSource || '') !== 'Club';

  useEffect(() => {
    if (shouldSuppressDetailModalForSelectedSlot && showDetailModal) {
      setShowDetailModal(false);
      setAppointmentDetail(null);
    }
  }, [shouldSuppressDetailModalForSelectedSlot, showDetailModal]);

  useEffect(() => {
    const fetchTransferSlots = async () => {
      if (!clubTransferDate || !clubTransferData?.facilityId) {
        setClubAvailableTransferSlots([]);
        return;
      }
      setClubAvailableTransferSlotsLoading(true);
      try {
        const dateStr = dayjs(clubTransferDate).format('YYYY-MM-DD');
        const res = await api.get(`/api/sports/available-slots/${clubTransferData.facilityId}`, { 
          params: { date: dateStr } 
        });
        const data = res.data?.data || res.data || [];
        setClubAvailableTransferSlots(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Müsait saatleri çekerken hata:', err);
        setClubAvailableTransferSlots([]);
      } finally {
        setClubAvailableTransferSlotsLoading(false);
      }
    };
    fetchTransferSlots();
  }, [clubTransferDate, clubTransferData]);

  const handleTransfer = async () => {
    if (!clubTransferData || !clubTransferDate) {
      showToast('Lütfen bir hedef tarih seçin.', 'error');
      return;
    }
    if (!clubTransferStartTime || !clubTransferEndTime) {
      showToast('Lütfen bir hedef saat dilimi seçin.', 'error');
      return;
    }
    setClubTransferLoading(true);
    try {
      const payload = {
        assignmentId: clubTransferData.assignmentId,
        originalDate: dayjs(clubTransferData.originalDate).format('YYYY-MM-DD'),
        newDate: dayjs(clubTransferDate).format('YYYY-MM-DD'),
        newStartTime: clubTransferStartTime,
        newEndTime: clubTransferEndTime,
        notes: clubTransferNotes
      };
      const response = await transferSession(payload);
      if (response?.isSuccess !== false) {
        showToast('Seans başarıyla aktarıldı!', 'success');
        setClubTransferModalVisible(false);
        if (paymentModalApp && paymentModalApp.taxNumber) {
          const refresh = await getClubPaymentInfo(paymentModalApp.taxNumber, {
            clubApplicationId: paymentModalApp.id,
            facilityId: paymentModalApp.facilityId
          });
          if (refresh?.isSuccess !== false && refresh?.data) {
            setPaymentModalData(refresh.data);
          }
        }
        // Refresh main UI components
        fetchAllAppointments();
        fetchAvailableTimeSlots();
      } else {
        showToast(response?.resultMessage || 'Aktarım işlemi başarısız.', 'error');
      }
    } catch (err) {
      console.error('Aktarım hatası:', err);
      showToast('Aktarım sırasında hata oluştu.', 'error');
    } finally {
      setClubTransferLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="sports-appointments">
        <div className="page-header">
          <h2>Spor Randevu Sistemi</h2>
        </div>
        <div className="loading-message">
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sports-appointments">
        <div className="page-header">
          <h2>Spor Randevu Sistemi</h2>
        </div>
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchRegionsAndFacilities} className="retry-button">Tekrar Dene</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sports-appointments">
      <div className="page-header">
        <div className="header-left">
          <h2>Spor Randevu Sistemi</h2>
        </div>
        <div className="management-actions">
          {activeView === 'appointments' ? (
            <>
              {canViewHoursManagement && (
                <button
                  onClick={() => setActiveView('pricing')}
                  className={`mgmt-btn mgmt-btn--success ${activeView === 'pricing' ? 'active' : ''}`}
                >
                  <FaClock /> Saatler
                </button>
              )}
              {userRole !== 'istirakler' && (
                <button
                  onClick={() => setActiveView('blocked-slots')}
                  className={`mgmt-btn mgmt-btn--danger ${activeView === 'blocked-slots' ? 'active' : ''}`}
                >
                  <FaCalendarAlt /> Takvim
                </button>
              )}
              {userRole !== 'istirakler' && (
                <button
                  onClick={() => setActiveView('club-applications')}
                  className={`mgmt-btn mgmt-btn--info ${activeView === 'club-applications' ? 'active' : ''}`}
                >
                  <FaInfoCircle /> Kulüp Bilgisi
                </button>
              )}
              {userRole !== 'istirakler' && (
                <button
                  onClick={() => setActiveView('summer-sports')}
                  className={`mgmt-btn mgmt-btn--warning ${activeView === 'summer-sports' ? 'active' : ''}`}
                >
                  <FaSun /> Yaz Kursları
                </button>
              )}
              {hasRaufAccess && (
                <button
                  onClick={() => setActiveView('tennis-academy')}
                  className={`mgmt-btn mgmt-btn--primary ${activeView === 'tennis-academy' ? 'active' : ''}`}
                >
                  <GiTennisRacket /> Tenis Akademisi
                </button>
              )}

              {canManage && userRole !== 'istirakler' && (
                <>
                  <button
                    onClick={() => setActiveView('regions')}
                    className={`mgmt-btn mgmt-btn--warning ${activeView === 'regions' ? 'active' : ''}`}
                  >
                    <FaMapMarkedAlt /> Bölgeler
                  </button>
                  <button
                    onClick={() => setActiveView('facilities')}
                    className={`mgmt-btn mgmt-btn--teal ${activeView === 'facilities' ? 'active' : ''}`}
                  >
                    <FaUniversity /> Tesisler
                  </button>
                </>
              )}
              {(canViewCardManagement || userRole === 'istirakler') && (
                <>
                  <button
                    onClick={() => setActiveView('card-applications')}
                    className={`mgmt-btn mgmt-btn--primary ${activeView === 'card-applications' ? 'active' : ''}`}
                  >
                    <FaAddressCard /> Kart Başvuruları
                  </button>
                  {hasFitnessAccess && (
                    <button
                      onClick={() => setActiveView('women-training')}
                      className={`mgmt-btn mgmt-btn--primary ${activeView === 'women-training' ? 'active' : ''}`}
                      style={activeView === 'women-training' ? { background: '#ec4899', borderColor: '#be185d' } : { borderColor: '#ec4899', color: '#ec4899' }}
                    >
                      <FaFemale /> Kadın Eğitim
                    </button>
                  )}
                  <button
                    onClick={() => setActiveView('balance-control')}
                    className={`mgmt-btn mgmt-btn--slate ${activeView === 'balance-control' ? 'active' : ''}`}
                  >
                    <FaWallet /> Bakiye Kontrol
                  </button>
                </>
              )}
              {canViewPaymentHistory && (
                <button
                  onClick={() => setActiveView('payment-history')}
                  className={`mgmt-btn mgmt-btn--success ${activeView === 'payment-history' ? 'active' : ''}`}
                >
                  <FaMoneyBillWave /> Ödeme Geçmişi
                </button>
              )}
              {canManageMembershipHoliday && userRole !== 'istirakler' && (
                <button
                  onClick={() => setActiveView('membership-holiday')}
                  className={`mgmt-btn mgmt-btn--warning ${activeView === 'membership-holiday' ? 'active' : ''}`}
                >
                  <FaCalendarAlt /> Tatil Günü Ekle
                </button>
              )}
              {(isSportsAdminRole || isRegionalUser) && userRole !== 'istirakler' && (
                <button
                  onClick={() => setActiveView('how-to-guide')}
                  className={`mgmt-btn mgmt-btn--info ${activeView === 'how-to-guide' ? 'active' : ''}`}
                >
                  <FaQuestionCircle /> Nasıl Yapılır?
                </button>
              )}
            </>
          ) : (
            <button
              onClick={() => setActiveView('appointments')}
              className="mgmt-btn mgmt-btn--slate"
              style={{ marginLeft: 'auto' }}
            >
              <FaArrowLeft /> Randevulara Dön
            </button>
          )}
        </div>
      </div>

      {activeView === 'appointments' && (
        <div className="sports-content">
          <div className="left-panel">
            <div className="regions-list">
              {regions.map(region => (
                <div key={region.id} className="region-item">
                  <button
                    className={`region-header ${expandedRegions.includes(region.id) ? 'expanded' : ''}`}
                    onClick={() => toggleRegion(region.id)}
                  >
                    <span className="region-icon">
                      {expandedRegions.includes(region.id) ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                    <span className="region-name">{region.name}</span>
                    <span className="region-count">
                      {(() => {
                        const isKort = (f) =>
                          f.type?.toLowerCase() === 'tenis' ||
                          f.name?.toLowerCase().includes('kort');
                        const kortCount = region.facilities.filter(isKort).length;
                        const sahaCount = region.facilities.length - kortCount;
                        const parts = [];
                        if (kortCount > 0) parts.push(`${kortCount} Kort`);
                        if (sahaCount > 0) parts.push(`${sahaCount} Saha`);
                        return parts.length > 0 ? parts.join(', ') : '0 Saha';
                      })()}
                    </span>
                  </button>

                  {expandedRegions.includes(region.id) && (
                    <div className="facilities-list">
                      {region.facilities.map(facility => {
                        const Icon = facilityIcons[facility.type] || FaFutbol;
                        const isSelected = selectedFacility?.id === facility.id;

                        return (
                          <button
                            key={facility.id}
                            className={`facility-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleFacilityClick(facility, region)}
                          >
                            <div className="facility-icon">
                              <Icon />
                            </div>
                            <div className="facility-info">
                              <div className="facility-name">{facility.name}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="right-panel">
            {selectedFacility ? (
              <div className="facility-details">
                <div className="facility-details-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="facility-title">
                    <h3>{selectedFacility.name}</h3>
                    <span className="facility-region">{selectedFacility.regionName}</span>
                  </div>
                  {(canManage || isRegionalUser) && process.env.NODE_ENV === 'development' && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="mgmt-btn mgmt-btn--success"
                        onClick={() => handleDownloadBlockingHistory(selectedFacility.id, selectedFacility.name)}
                        disabled={downloadingHistory}
                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}
                      >
                        <FaPrint /> {downloadingHistory ? 'İndiriliyor...' : 'Kapatma Geçmişi Excel'}
                      </button>
                      <button
                        className="mgmt-btn mgmt-btn--primary"
                        onClick={() => handleDownloadClubAssignmentHistory(selectedFacility.id, selectedFacility.name)}
                        disabled={downloadingClubHistory}
                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}
                      >
                        <FaPrint /> {downloadingClubHistory ? 'İndiriliyor...' : 'Kulüp Atama Geçmişi Excel'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="facility-specs">
                  <div className="spec-item">
                    <span className="spec-label">Tür</span>
                    <span className="spec-value">{selectedFacility.type}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Kapasite</span>
                    <span className="spec-value">{selectedFacility.capacity}</span>
                  </div>
                </div>

                <div className="appointment-section">
                  <div className="date-picker-section">
                    <label>Tarih Seçin</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="date-input"
                    />
                  </div>

                  <div className="time-slots-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h4 style={{ margin: 0 }}>Müsait Saatler</h4>
                      {selectedFacility.isSharedFacility && (
                        <select
                          value={selectedSportTypeId || ''}
                          onChange={(e) => setSelectedSportTypeId(parseInt(e.target.value))}
                          style={{ padding: '5px', borderRadius: '4px', border: '1px solid #d1d3e2' }}
                        >
                          {selectedFacility.availableSports?.map(sport => (
                            <option key={sport.typeId} value={sport.typeId}>{sport.typeName}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="time-slots-grid">
                      {selectedFacility.timeSlots.map((slot, index) => {
                        const isFitnessFacility = String(selectedFacility?.type || '').toLowerCase().includes('fitness');
                        const isClubSlot = !slot.available && slot.blockSource === 'Club';
                        const isManagementBlocked =
                          !slot.available &&
                          (slot.blockSource === 'Maintenance' ||
                            slot.blockSource === 'PublicEducation' ||
                            slot.blockSource === 'PublicEducationCourse' ||
                            slot.status === 'Blocked');
                        const isMaleSlot = slot.allowedGender === 'Male';
                        const isFemaleSlot = slot.allowedGender === 'Female';
                        const occupancy = getSlotOccupancyState(slot, selectedFacility);
                        const showOccupancy = occupancy.capacity > 1;
                        const shouldUseCapacityInlineFlow =
                          occupancy.capacity > 1 &&
                          !isClubSlot &&
                          !isManagementBlocked &&
                          (isFitnessFacility || occupancy.currentCount > 0);
                        const occupancyLabel = `${occupancy.currentCount}/${occupancy.capacity}`;

                        let genderStyle = {};
                        if (slot.available && isMaleSlot) {
                          genderStyle = { background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', borderColor: '#93c5fd' };
                        } else if (slot.available && isFemaleSlot) {
                          genderStyle = { background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)', borderColor: '#f9a8d4' };
                        }

                        return (
                          <button
                            key={index}
                            className={`time-slot ${slot.available ? 'available' : (slot.status?.toLowerCase() || 'occupied')} ${selectedTimeSlot?.time === slot.time ? 'selected' : ''}`}
                            onClick={() => (isFitnessFacility && !isClubSlot && !isManagementBlocked)
                              ? handleCapacitySlotClick(slot)
                              : (shouldUseCapacityInlineFlow ? handleCapacitySlotClick(slot) : handleTimeSlotClick(slot))}
                            style={isClubSlot ? {
                              background: '#1cc88a',
                              borderColor: '#13855c',
                              color: 'white'
                            } : genderStyle}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span className="slot-time">{slot.time}</span>
                              {isMaleSlot && <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>♂</span>}
                              {isFemaleSlot && <span style={{ color: '#ec4899', fontWeight: 'bold' }}>♀</span>}
                            </div>
                            <span className="slot-status">
                              {slot.available
                                ? (isMaleSlot ? 'Erkek Saati' : isFemaleSlot ? 'Kadın Saati' : 'Müsait')
                                : (isClubSlot ? 'Kulüp Saati' : 'Dolu')}
                            </span>
                            {showOccupancy && (
                              <span className="slot-status" style={{ color: slot.available ? '#0f766e' : '#b91c1c', fontWeight: 700 }}>
                                {slot.available ? `${occupancyLabel} dolu` : `${occupancyLabel} doldu`}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {selectedTimeSlot && slotAppointments.length > 0 && (
                      <div style={{ marginTop: '16px', padding: '14px 16px', border: '1px solid #e5e7eb', borderRadius: '12px', background: '#fff' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937', marginBottom: '10px' }}>
                          Seçilen Saatteki Randevular ({slotAppointments.length})
                        </div>
                        <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '12px' }}>
                          {selectedDate} | {selectedTimeSlot.time}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                              <th style={{ textAlign: 'left', padding: '8px 10px' }}>Ad Soyad</th>
                              <th style={{ textAlign: 'left', padding: '8px 10px' }}>TC</th>
                              <th style={{ textAlign: 'left', padding: '8px 10px' }}>Telefon</th>
                            </tr>
                          </thead>
                          <tbody>
                            {slotAppointments.map((appt, idx) => (
                              <tr key={appt.id || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '8px 10px' }}>{appt.firstName} {appt.lastName}</td>
                                <td style={{ padding: '8px 10px' }}>{appt.identityNumber || '-'}</td>
                                <td style={{ padding: '8px 10px' }}>{appt.phone || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {selectedTimeSlot && canCreateManualAppointment && (
                    <div className="booking-form" ref={formRef}>
                      <h4>Manuel Randevu Oluştur</h4>
                      <div className="form-grid">
                        {selectedFacility.allowClubReservations && (
                          <div className="form-group" style={{ gridColumn: '1 / -1', padding: '10px', background: '#f8f9fc', borderRadius: '8px', border: '1px solid #e3e6f0' }}>
                            <label style={{ marginBottom: '10px', display: 'block' }}>Rezervasyon Tipi</label>
                            <div style={{ display: 'flex', gap: '15px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="reservationType"
                                  value="Individual"
                                  checked={reservationType === 'Individual'}
                                  onChange={() => setReservationType('Individual')}
                                />
                                Bireysel
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="reservationType"
                                  value="Club"
                                  checked={reservationType === 'Club'}
                                  onChange={() => setReservationType('Club')}
                                />
                                Kulüp / Takım
                              </label>
                            </div>

                            {reservationType === 'Club' && (
                              <div style={{ marginTop: '10px' }}>
                                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '5px' }}>Kişi Sayısı</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={personCount}
                                    onChange={(e) => {
                                      const val = parseInt(e.target.value);
                                      if (val > 0) setPersonCount(val);
                                      else if (e.target.value === '') setPersonCount('');
                                    }}
                                    style={{ width: '80px', padding: '5px', borderRadius: '4px', border: '1px solid #d1d3e2' }}
                                  />
                                  <span style={{ fontSize: '0.85rem', color: '#666', background: '#fff', padding: '2px 8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                    Kişi Başı: <strong>{selectedFacility.clubPricePerPerson} TL</strong>
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                          <label htmlFor="userGender">Cinsiyet <span className="required">*</span></label>
                          <select
                            id="userGender"
                            value={formData.userGender}
                            onChange={(e) => setFormData({ ...formData, userGender: e.target.value })}
                            className="form-input"
                            style={{ width: '100%' }}
                          >
                            <option value="">Seçiniz</option>
                            <option value="Male">Erkek</option>
                            <option value="Female">Kadın</option>
                            {reservationType === 'Club' && <option value="Mixed">Karma</option>}
                          </select>
                        </div>
                        <div className="form-group">
                          <label htmlFor="firstName">Ad <span className="required">*</span></label>
                          <input
                            id="firstName"
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            placeholder="Adını giriniz"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="lastName">Soyad <span className="required">*</span></label>
                          <input
                            id="lastName"
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            placeholder="Soyadını giriniz"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="idNumber">T.C. Kimlik No <span className="required">*</span></label>
                          <input
                            id="idNumber"
                            type="text"
                            value={formData.idNumber}
                            onChange={(e) => setFormData({ ...formData, idNumber: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                            placeholder="TC Kimlik No giriniz"
                            className="form-input"
                            maxLength="11"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="email">E-Posta <span className="required">*</span></label>
                          <input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="E-posta adresi"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="phone">Telefon <span className="required">*</span></label>
                          <input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="5XXXXXXXXX"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group appointment-summary">
                          <label>Randevu Bilgileri</label>
                          <div className="summary-box">
                            <div className="summary-item">
                              <strong>Saha:</strong> {selectedFacility.name}
                            </div>
                            <div className="summary-item">
                              <strong>Tarih:</strong> {new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </div>
                            <div className="summary-item">
                              <strong>Saat:</strong> {selectedTimeSlot.time}
                            </div>
                            <div className="summary-item">
                              <strong>Tip:</strong> {reservationType === 'Club' ? 'Kulüp / Takım' : 'Bireysel'}
                            </div>
                            {reservationType === 'Club' && (
                              <div className="summary-item">
                                <strong>Kişi Sayısı:</strong> {personCount}
                              </div>
                            )}
                            <div className="summary-item">
                              <strong>Tutar:</strong> {reservationType === 'Club'
                                ? (selectedFacility.clubPricePerPerson ? (selectedFacility.clubPricePerPerson * (personCount || 1)).toFixed(2) : '0.00')
                                : (selectedTimeSlot.price ? Number(selectedTimeSlot.price).toFixed(2) : '0.00')} TL
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="submit-button"
                        onClick={handleCreateAppointment}
                      >
                        Randevu Kaydet
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="no-selection">
                <FaFutbol className="no-selection-icon" />
                <h3>Saha Seçin</h3>
                <p>Randevu almak için sol panelden bölge ve saha seçiniz</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeView === 'appointments' && (
        <div className="appointments-list-section">
          <div className="section-header">
            <h3>Alınan Randevular</h3>
            <span className="appointment-count">{filteredAppointments.length} randevu</span>
          </div>

          <div className="appointments-filter-bar">
            <div className="filter-tabs">
              <button className={`filter-tab ${appointmentTab === 'all' ? 'active' : ''}`} onClick={() => setAppointmentTab('all')}>
                Tümü <span className="filter-badge">{appointments.length}</span>
              </button>
              <button className={`filter-tab filter-tab--confirmed ${appointmentTab === 'confirmed' ? 'active' : ''}`} onClick={() => setAppointmentTab('confirmed')}>
                Onayli <span className="filter-badge">{confirmedCount}</span>
              </button>
              <button className={`filter-tab filter-tab--failed ${appointmentTab === 'paymentFailed' ? 'active' : ''}`} onClick={() => setAppointmentTab('paymentFailed')}>
                Ödeme Alınamadı <span className="filter-badge">{paymentFailedCount}</span>
              </button>
              <button className={`filter-tab filter-tab--failed ${appointmentTab === 'cancelled' ? 'active' : ''}`} onClick={() => setAppointmentTab('cancelled')}>
                İptal Edilenler <span className="filter-badge">{cancelledCount}</span>
              </button>
              <button className={`filter-tab filter-tab--failed ${appointmentTab === 'refunded' ? 'active' : ''}`} onClick={() => setAppointmentTab('refunded')}>
                İade İşlemleri <span className="filter-badge">{refundedCount}</span>
              </button>
            </div>
            <div className="filter-search">
              <select
                className="sort-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="newest">En Yeni Randevular</option>
                <option value="oldest">En Eski Randevular</option>
              </select>
              <input
                type="text"
                placeholder="İsim veya TC ile ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="filter-search-input"
              />
            </div>
          </div>

          <div className="appointments-table-wrapper desktop-only">
            <table className="appointments-table">
              <thead>
                <tr>
                  <th>AD SOYAD</th>
                  <th>TC</th>
                  <th>TARİH</th>
                  <th>SAAT</th>
                  <th>BÖLGE</th>
                  <th>SAHA</th>
                  <th>DURUM</th>
                  <th>ÖDEME</th>
                  <th>E-POSTA</th>
                  <th>TELEFON</th>
                  <th>OLUŞTURMA</th>
                  <th>AKTAR</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAppointments.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="no-data">
                      {appointmentTab === 'all' ? 'Henüz randevu bulunmamaktadır.' :
                        appointmentTab === 'confirmed' ? 'Onaylı randevu bulunmamaktadır.' :
                          appointmentTab === 'cancelled' ? 'İptal edilmiş randevu bulunmamaktadır.' :
                            appointmentTab === 'refunded' ? 'İade işlemi bulunmamaktadır.' :
                              'Ödeme alınamadı kaydı bulunmamaktadır.'}
                    </td>
                  </tr>
                ) : (
                  paginatedAppointments.map((appointment, index) => (
                    <tr key={appointment.id || index}>
                      <td className="name-cell">{appointment.firstName} {appointment.lastName}</td>
                      <td>{appointment.identityNumber}</td>
                      <td>{new Date(appointment.appointmentDate).toLocaleDateString('tr-TR')}</td>
                      <td>{appointment.startTime} - {appointment.endTime}</td>
                      <td>{appointment.regionName}</td>
                      <td>{appointment.facilityName}</td>
                      <td>
                        {['Cancelled', 'CancelledNoRefund'].includes(appointment.status) ? (
                          <span className="status-badge" style={{ background: '#fee2e2', color: '#dc2626' }}>İPTAL EDİLDİ</span>
                        ) : ['Refunded', 'RefundRequested'].includes(appointment.status) ? (
                          <span className="status-badge" style={{ background: '#fef3c7', color: '#d97706' }}>İADE İŞLEMİ</span>
                        ) : (
                          <span className={`status-badge ${isPaymentApproved(appointment) ? 'confirmed' : 'payment-failed'}`}>
                            {isCreditAppointment(appointment) ? 'KONTÖRLÜ' : (isPaymentApproved(appointment) ? 'ONAYLANDI' : 'ÖDEME ALINAMADI')}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="payment-cell">
                          <span className={`payment-badge ${isPaymentApproved(appointment) ? 'approved' : 'payment-failed'}`}>
                            {getAppointmentPaymentLabel(appointment)}
                          </span>
                        </div>
                      </td>
                      <td className="email-cell">{appointment.email}</td>
                      <td>{appointment.phone}</td>
                      <td>{new Date(appointment.createdDate).toLocaleString('tr-TR')}</td>
                      <td>
                        <div className="appointment-actions">
                          <button
                            type="button"
                            className="action-button transfer"
                            disabled={actionLoadingId === appointment.id}
                            onClick={() => handleTransferClick(appointment)}
                          >
                            Aktar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="appointments-cards-mobile">
            {paginatedAppointments.length === 0 ? (
              <div className="no-data-card">
                {appointmentTab === 'all' ? 'Henüz randevu bulunmamaktadır.' :
                  appointmentTab === 'confirmed' ? 'Onaylı randevu bulunmamaktadır.' :
                    'Ödeme alınamadı kaydı bulunmamaktadır.'}
              </div>
            ) : (
              paginatedAppointments.map((appointment, index) => (
                <div key={appointment.id || index} className="appointment-card">
                  <div className="card-header">
                    <div className="card-name">{appointment.firstName} {appointment.lastName}</div>
                    <span className={`status-badge ${isPaymentApproved(appointment) ? 'confirmed' : 'payment-failed'}`}>
                      {isCreditAppointment(appointment) ? 'KONTÖRLÜ' : (isPaymentApproved(appointment) ? 'ONAYLANDI' : 'ÖDEME ALINAMADI')}
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="card-info-row">
                      <span className="card-label">TC:</span>
                      <span className="card-value">{appointment.identityNumber}</span>
                    </div>
                    <div className="card-info-row">
                      <span className="card-label">Tarih:</span>
                      <span className="card-value">{new Date(appointment.appointmentDate).toLocaleDateString('tr-TR')}</span>
                    </div>
                    <div className="card-info-row">
                      <span className="card-label">Saat:</span>
                      <span className="card-value">{appointment.startTime} - {appointment.endTime}</span>
                    </div>
                    <div className="card-info-row">
                      <span className="card-label">Saha:</span>
                      <span className="card-value">{appointment.facilityName}</span>
                    </div>
                    <div className="card-info-row">
                      <span className="card-label">Bölge:</span>
                      <span className="card-value">{appointment.regionName}</span>
                    </div>
                    <div className="card-info-row">
                      <span className="card-label">Ödeme:</span>
                      <span className={`card-value payment-badge ${isPaymentApproved(appointment) ? 'approved' : 'payment-failed'}`}>
                        {getAppointmentPaymentLabel(appointment)}
                      </span>
                    </div>
                    <div className="card-info-row">
                      <span className="card-label">Telefon:</span>
                      <span className="card-value">{appointment.phone}</span>
                    </div>
                  </div>
                  <div className="card-footer">
                    <span className="card-date">{new Date(appointment.createdDate).toLocaleString('tr-TR')}</span>
                    <button
                      type="button"
                      className="action-button transfer"
                      disabled={actionLoadingId === appointment.id}
                      onClick={() => handleTransferClick(appointment)}
                    >
                      Aktar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-wrapper">
              <button
                className="pagination-btn pagination-nav-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                title="Önceki Sayfa"
              >
                <FaChevronDown style={{ transform: 'rotate(90deg)' }} />
              </button>

              <div className="pagination-pages">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && <span className="pagination-ellipsis">...</span>}
                      <button
                        className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))
                }
              </div>

              <button
                className="pagination-btn pagination-nav-btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                title="Sonraki Sayfa"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>
      )}

      {showDetailModal && !shouldSuppressDetailModalForSelectedSlot && !appointmentDetail && (
        <div className="appointment-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="appointment-modal" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', padding: '40px' }}>
            <p>Yükleniyor...</p>
          </div>
        </div>
      )}

      {showDetailModal && !shouldSuppressDetailModalForSelectedSlot && appointmentDetail && (
        <div className="appointment-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="appointment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={
              appointmentDetail.reservationType === 'CapacitySummary' ? { background: 'linear-gradient(135deg, #ec4899, #db2777)', color: 'white' } :
                appointmentDetail.blockSource === 'Club' ? { background: 'linear-gradient(135deg, #1cc88a, #17a673)', color: 'white' } :
                  (appointmentDetail.blockSource === 'Maintenance' || appointmentDetail.blockSource === 'PublicEducation' || appointmentDetail.blockSource === 'PublicEducationCourse' || appointmentDetail.reservationType === 'Blocked') ? { background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white' } :
                    { background: 'linear-gradient(135deg, #4e73df, #224abe)', color: 'white' }
            }>
              <h3>{
                appointmentDetail.reservationType === 'CapacitySummary' ? 'Kontenjan Durumu' :
                  appointmentDetail.blockSource === 'Club' ? 'Kulüp Slot Detayı' :
                    (appointmentDetail.reservationType === 'Blocked') ? 'Kapatılmış Saat Detayı' : 'Randevu Detayları'
              }</h3>
              <button className="modal-close" onClick={() => setShowDetailModal(false)} style={{ color: 'white' }}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {appointmentDetail.reservationType === 'CapacitySummary' ? (
                <>
                  <div style={{ padding: '12px 16px', background: '#fdf2f8', borderRadius: '10px', marginBottom: '12px' }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: '#9d174d', marginBottom: '4px' }}>
                      {appointmentDetail.sportTypeName || 'Kontenjan Slotu'}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#be185d' }}>
                      {(appointmentDetail.currentCount || 0)} / {(appointmentDetail.capacity || 1)} doluluk
                    </div>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Tesis:</span>
                    <span className="detail-value">{appointmentDetail.regionName} / {appointmentDetail.facilityName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Saat:</span>
                    <span className="detail-value">{appointmentDetail.startTime} - {appointmentDetail.endTime}</span>
                  </div>

                  {/* Randevu Listesi */}
                  <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#374151', marginBottom: '10px' }}>
                      Randevu Alan Kişiler ({slotAppointments.length})
                    </div>
                    {slotAppointments.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '0.85rem' }}>
                        {appointmentDetail.currentCount > 0 ? 'Yükleniyor...' : 'Henüz randevu alınmamış.'}
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                            <th style={{ textAlign: 'left', padding: '8px 10px', color: '#6b7280', fontWeight: 600 }}>#</th>
                            <th style={{ textAlign: 'left', padding: '8px 10px', color: '#6b7280', fontWeight: 600 }}>İsim Soyisim</th>
                            <th style={{ textAlign: 'center', padding: '8px 10px', color: '#6b7280', fontWeight: 600 }}>Ödeme</th>
                          </tr>
                        </thead>
                        <tbody>
                          {slotAppointments.map((appt, idx) => {
                            const paid = (appt.paymentStatus || '').toLowerCase() === 'approved';
                            return (
                              <tr key={appt.id || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '8px 10px', color: '#9ca3af' }}>{idx + 1}</td>
                                <td style={{ padding: '8px 10px', color: '#111827', fontWeight: 500 }}>
                                  {appt.firstName} {appt.lastName}
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '3px 10px',
                                    borderRadius: '12px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    background: paid ? '#d1fae5' : '#fee2e2',
                                    color: paid ? '#065f46' : '#991b1b'
                                  }}>
                                    {paid ? 'Ödendi' : 'Ödenmedi'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              ) : (
                <PremiumAppointmentDetailView appointmentDetail={appointmentDetail} />
              )}
            </div>
            {appointmentDetail.blockSource !== 'Club' && appointmentDetail.reservationType !== 'CapacitySummary' && (
              <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                {(appointmentDetail.paymentStatus || '').toLowerCase() === 'approved' && (
                  <button
                    type="button"
                    className="action-button"
                    style={{ background: '#1cc88a', color: 'white', border: 'none' }}
                    onClick={() => {
                      setReceiptData({
                        isClub: false,
                        transactionId: appointmentDetail.paymentTransactionId || appointmentDetail.id,
                        amount: appointmentDetail.paymentAmount || 0,
                        createdAt: appointmentDetail.appointmentDate,
                        firstName: appointmentDetail.firstName,
                        lastName: appointmentDetail.lastName,
                        identityNumber: appointmentDetail.identityNumber,
                        facilityName: `${appointmentDetail.regionName} / ${appointmentDetail.facilityName}`,
                        appointmentId: appointmentDetail.appointmentCode || appointmentDetail.id,
                        message: 'Spor Randevusu Ödemesi'
                      });
                      setTimeout(() => {
                        window.print();
                      }, 200);
                    }}
                  >
                    <FaPrint style={{ marginRight: '5px' }} /> Dekont Al
                  </button>
                )}
                <button
                  type="button"
                  className="action-button transfer"
                  disabled={actionLoadingId === appointmentDetail.id}
                  onClick={() => {
                    setShowDetailModal(false);
                    handleTransferClick(appointmentDetail);
                  }}
                >
                  Aktar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && transferAppointment && (
        <div className="appointment-modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="appointment-modal transfer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Randevu Aktar</h3>
              <button className="modal-close" onClick={() => setShowTransferModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <div className="transfer-info">
                <div className="detail-row">
                  <span className="detail-label">Ad Soyad:</span>
                  <span className="detail-value">{transferAppointment.firstName} {transferAppointment.lastName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Saha:</span>
                  <span className="detail-value">{transferAppointment.facilityName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Mevcut Tarih:</span>
                  <span className="detail-value">{new Date(transferAppointment.appointmentDate).toLocaleDateString('tr-TR')}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Mevcut Saat:</span>
                  <span className="detail-value">{transferAppointment.startTime} - {transferAppointment.endTime}</span>
                </div>
              </div>

              <div className="transfer-date-selector">
                <h4>Aktarılacak Tarih</h4>
                <div className="transfer-date-nav">
                  <button type="button" className="nav-btn" onClick={handlePrevDay}>Onceki Gun</button>
                  <input
                    type="date"
                    value={transferDate}
                    onChange={(e) => handleTransferDateChange(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="date-input"
                  />
                  <button type="button" className="nav-btn" onClick={handleNextDay}>Sonraki Gun</button>
                </div>
              </div>

              <div className="transfer-slots-section">
                <h4>Müsait Saatler – {new Date(transferDate).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h4>
                {transferLoading ? (
                  <div className="transfer-loading">Saatler yükleniyor...</div>
                ) : (
                  <div className="transfer-slots-grid">
                    {(Array.isArray(transferSlots) ? transferSlots : []).filter(slot => slot.available).length === 0 ? (
                      <div className="no-slots-msg">Bu tarihte müsait saat bulunmamaktadır. Sonraki güne geçiniz.</div>
                    ) : (
                      (Array.isArray(transferSlots) ? transferSlots : []).filter(slot => slot.available).map((slot, index) => {
                        const [startTime, endTime] = slot.time.split(' - ');
                        return (
                          <button
                            key={index}
                            type="button"
                            className="transfer-slot-btn"
                            disabled={actionLoadingId === transferAppointment.id}
                            onClick={() => handleTransferAppointment(transferDate, startTime, endTime)}
                          >
                            {slot.time}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {activeView === 'regions' && <RegionsManagement userRegions={regions} />}
      {activeView === 'facilities' && <FacilitiesManagement userRegions={regions} fitnessAccess={hasFitnessAccess} />}
      {activeView === 'pricing' && canViewHoursManagement && (
        <PricingManagement userRegions={regions} fitnessAccess={hasFitnessAccess} />
      )}
      {activeView === 'blocked-slots' && <BlockedSlotsManagement userRegions={regions} showToast={showToast} fitnessAccess={hasFitnessAccess} />}
      {/* {activeView === 'cancellations' && <RefundApplicationsList userRegions={regions} />} */}
      {/* {activeView === 'public-education' && <div style={{ width: '100%' }}><PublicEducationManagement /></div>} */}
      {activeView === 'summer-sports' && <SummerSportsApplicationsList showToast={showToast} />}
      {activeView === 'tennis-academy' && <div style={{ width: '100%' }}><TennisAcademyManagement showToast={showToast} /></div>}

      {activeView === 'membership-holiday' && canManageMembershipHoliday && (
        <div style={{ maxWidth: '920px', margin: '1.5rem auto', padding: '0 1rem' }}>
          <div style={{
            background: '#fff',
            borderRadius: '14px',
            border: '1px solid #dbeafe',
            boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.85rem',
              padding: '1.25rem 1.5rem',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <span style={{
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                background: '#0f75bc',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.05rem',
                flexShrink: 0
              }}>
                <FaCalendarAlt />
              </span>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a' }}>Tatil Günü Ekle</h3>
                <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
                  Hizmet verilemeyen resmi tatil günlerini aktif üyeliklerin bitiş tarihine ekleyin.
                </p>
              </div>
            </div>

            <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <label style={{ display: 'grid', gap: '0.4rem', color: '#334155', fontWeight: 700, fontSize: '0.9rem' }}>
                Tesis
                <select
                  className="form-control"
                  style={{ minHeight: '42px', borderRadius: '8px', borderColor: '#cbd5e1' }}
                  value={membershipHolidayForm.programType}
                  onChange={(event) => setMembershipHolidayForm((prev) => ({ ...prev, programType: event.target.value }))}
                >
                  <option value="Fitness">Fitness</option>
                  <option value="Pool">Havuz</option>
                </select>
              </label>

              <label style={{ display: 'grid', gap: '0.4rem', color: '#334155', fontWeight: 700, fontSize: '0.9rem' }}>
                Eklenecek Gün Sayısı
                <input
                  type="number"
                  min="1"
                  max="60"
                  className="form-control"
                  style={{ minHeight: '42px', borderRadius: '8px', borderColor: '#cbd5e1' }}
                  value={membershipHolidayForm.dayCount}
                  onChange={(event) => setMembershipHolidayForm((prev) => ({ ...prev, dayCount: event.target.value }))}
                />
              </label>
            </div>

            <label style={{ display: 'grid', gap: '0.4rem', color: '#334155', fontWeight: 700, fontSize: '0.9rem', marginTop: '1rem' }}>
              Açıklama
              <input
                type="text"
                className="form-control"
                style={{ minHeight: '42px', borderRadius: '8px', borderColor: '#cbd5e1' }}
                value={membershipHolidayForm.description}
                onChange={(event) => setMembershipHolidayForm((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Örn: Resmi tatil nedeniyle tesis kapalıydı"
              />
            </label>

            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '10px',
              padding: '0.85rem 1rem',
              color: '#1e3a8a',
              fontSize: '0.9rem',
              lineHeight: 1.55,
              marginTop: '1rem'
            }}>
              Bu işlem sadece şu an tarih aralığı devam eden ve durumu aktif olan üyelikleri etkiler. Eski bitmiş üyelikler uzatılmaz.
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button
                type="button"
                onClick={submitMembershipHolidayExtension}
                disabled={membershipHolidayLoading}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: membershipHolidayLoading ? '#94a3b8' : '#0f75bc',
                  color: '#fff',
                  padding: '0.7rem 1rem',
                  fontWeight: 800,
                  cursor: membershipHolidayLoading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 8px 18px rgba(15, 117, 188, 0.22)'
                }}
              >
                <FaCalendarAlt /> {membershipHolidayLoading ? 'Ekleniyor...' : 'Günleri Ekle'}
              </button>
            </div>
          </div>
        </div>
        </div>
      )}

      {activeView === 'cancellations' && (
        <div className="appointments-list-section">
          <div className="section-header">
            <h3>İade Başvuruları</h3>
            <span className="appointment-count">
              {appointments.filter(a => a.status === 'Cancelled' || a.status === 'CancelledNoRefund' || a.status === 'Refunded').length} başvuru
            </span>
          </div>
          <div className="appointments-table-wrapper">
            <table className="appointments-table">
              <thead>
                <tr>
                  <th>AD SOYAD</th>
                  <th>TC</th>
                  <th>TARİH</th>
                  <th>SAAT</th>
                  <th>BÖLGE</th>
                  <th>SAHA</th>
                  <th>SPOR</th>
                  <th>DURUM</th>
                  <th>ÖDEME</th>
                  <th>E-POSTA</th>
                  <th>TELEFON</th>
                  <th>OLUŞTURMA</th>
                  <th>İŞLEMLER</th>
                </tr>
              </thead>
              <tbody>
                {appointments.filter(a => a.status === 'Cancelled' || a.status === 'CancelledNoRefund' || a.status === 'Refunded').length === 0 ? (
                  <tr>
                    <td colSpan="13" className="no-data">Henüz iptal/iade başvurusu bulunmamaktadır.</td>
                  </tr>
                ) : (
                  appointments
                    .filter(a => a.status === 'Cancelled' || a.status === 'CancelledNoRefund' || a.status === 'Refunded')
                    .map((appointment, index) => (
                      <tr key={appointment.id || index}>
                        <td className="name-cell">{appointment.firstName} {appointment.lastName}</td>
                        <td>{appointment.identityNumber}</td>
                        <td>{new Date(appointment.appointmentDate).toLocaleDateString('tr-TR')}</td>
                        <td>{appointment.startTime} - {appointment.endTime}</td>
                        <td>{appointment.regionName}</td>
                        <td>{appointment.facilityName}</td>
                        <td>{appointment.sportTypeName || '-'}</td>
                        <td>
                          <span className={`status-badge ${appointment.status?.toLowerCase()}`}>
                            {appointment.status === 'Cancelled' ? 'İPTAL (İade Yapıldı)' :
                              appointment.status === 'CancelledNoRefund' ? 'İPTAL (<24 saat)' :
                                appointment.status === 'Refunded' ? 'İADE EDİLDİ' : appointment.status}
                          </span>
                        </td>
                        <td>
                          <div className="payment-cell">
                            <span className={`payment-badge ${(appointment.paymentStatus || 'none').toLowerCase()}`}>
                              {appointment.paymentStatus ? appointment.paymentStatus.toUpperCase() : 'YOK'}
                            </span>
                            {appointment.paymentAmount ? (
                              <div className="payment-amount">
                                {Number(appointment.paymentAmount).toFixed(2)} TL
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td className="email-cell">{appointment.email}</td>
                        <td>{appointment.phone}</td>
                        <td>{new Date(appointment.createdDate).toLocaleString('tr-TR')}</td>
                        <td>
                          <div className="appointment-actions">
                            <button
                              type="button"
                              className="action-button detail"
                              onClick={() => {
                                setAppointmentDetail(appointment);
                                setShowDetailModal(true);
                              }}
                            >
                              Detay
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Club Applications View - Professionalized */}
      {activeView === 'club-applications' && (
        <div className="club-applications-section">
          <div className="section-header">
            <h3>Kulüp Başvuruları</h3>
            <small>Tüm tesislere ait kulüp başvuruları ve yönetim paneli</small>
          </div>

          <div className="appointment-filter-bar club-filter-toolbar" style={{ marginBottom: '1.5rem', background: '#f8f9fc', padding: '1rem', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${clubStatusFilter === 'approved' ? 'active' : ''}`}
                onClick={() => setClubStatusFilter('approved')}
              >
                Onaylandı
              </button>
              <button
                className={`filter-btn ${clubStatusFilter === 'pending' ? 'active' : ''}`}
                onClick={() => setClubStatusFilter('pending')}
              >
                Beklemede
              </button>
              <button
                className={`filter-btn ${clubStatusFilter === 'expired' ? 'active' : ''}`}
                onClick={() => setClubStatusFilter('expired')}
              >
                Süresi Bitenler
              </button>
              <button
                className={`filter-btn ${clubStatusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setClubStatusFilter('all')}
              >
                Tümü
              </button>
              {(userRole === 'admin' || userRole === '99') && (
                <button
                  className={`filter-btn ${clubStatusFilter === 'rejected' ? 'active' : ''}`}
                  onClick={() => setClubStatusFilter('rejected')}
                >
                  Reddedildi
                </button>
              )}
            </div>

            <label className="club-facility-filter">
              <span>Tesis</span>
              <select
                value={clubFacilityFilter}
                onChange={(event) => setClubFacilityFilter(event.target.value)}
                aria-label="Kulüp başvurularını tesise göre filtrele"
              >
                <option value="all">Tüm tesisler</option>
                {clubFacilityOptions.map(facility => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {clubApplicationsLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Kulüp başvuruları yükleniyor...</p>
            </div>
          ) : filteredClubApplications.length === 0 ? (
            <div className="empty-state-container" style={{ textAlign: 'center', padding: '5rem 2rem' }}>
              <FaFutbol style={{ fontSize: '5rem', color: '#e3e6f0', marginBottom: '1.5rem' }} />
              <h4 style={{ color: '#5a5c69', fontSize: '1.5rem' }}>Henüz Başvuru Yok</h4>
              <p style={{ color: '#858796' }}>Sistemde kayıtlı kulüp başvurusu bulunmamaktadır.</p>
            </div>
          ) : (
            <div className="club-applications-grid">
              {filteredClubApplications.map(app => (
                <div key={app.id} className="club-app-card">
                  <div className="card-header">
                    <h4 className="club-name">{app.firstName} {app.lastName}</h4>
                    <span className={`status-badge ${app.status?.toLowerCase()}`}>
                      {app.status === 'Pending' ? 'Beklemede' : app.status === 'Approved' ? 'Onaylandı' : 'Reddedildi'}
                    </span>
                  </div>

                  <div className="club-tags">
                    <span className="tag">{app.facilityName || getClubApplicationRegionName(app)}</span>
                    {app.sportType && <span className="tag sport-tag">{app.sportType}</span>}
                  </div>

                  <div className="info-grid">
                    <div className="info-item">
                      <strong>TESİS</strong>
                      {app.facilityName || 'Belirtilmemiş'}
                    </div>
                    <div className="info-item">
                      <strong>TC KİMLİK</strong>
                      {app.identityNumber}
                    </div>
                    <div className="info-item">
                      <strong>VERGİ NO</strong>
                      {app.taxNumber || '-'}
                    </div>
                    <div className="info-item">
                      <strong>TELEFON</strong>
                      {app.phone}
                    </div>
                    <div className="info-item" style={{ gridColumn: 'span 2' }}>
                      <strong>E-POSTA</strong>
                      {app.email}
                    </div>
                  </div>

                  {app.responsibleDays && (
                    <div className="responsible-section">
                      <label>Sorumluluk Günleri & Saatleri</label>
                      <div className="days-container">
                        {app.responsibleDays.split(/[,;]/).map((dayTime, idx) => (
                          <span key={idx} className="day-pill">{dayTime.trim()}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {app.status === 'Approved' && app.membershipMonths && (
                    <div className={`membership-info ${app.isExpired ? 'expired' : 'active'}`}>
                      <div className="membership-status">
                        <span>Üyelik Süresi ({app.membershipMonths} Ay)</span>
                        <span className={`status-label ${app.isExpired ? 'expired' : 'active'}`}>
                          {app.isExpired ? 'SÜRESİ DOLMUŞ' : 'AKTİF'}
                        </span>
                      </div>
                      {app.membershipStartDate && (
                        <div style={{ opacity: 0.8, fontWeight: 500 }}>
                          {new Date(app.membershipStartDate).toLocaleDateString('tr-TR')} - {new Date(app.membershipEndDate).toLocaleDateString('tr-TR')}
                        </div>
                      )}
                    </div>
                  )}

                  {app.notes && (
                    <div style={{ background: '#fffef0', border: '1px solid #ffeeba', padding: '0.75rem', borderRadius: '10px', fontSize: '0.85rem', marginBottom: '1.25rem', color: '#856404' }}>
                      <strong>Not:</strong> {app.notes}
                    </div>
                  )}

                  <div className="card-footer">
                    <div className="created-at">
                      Kayıt: {new Date(app.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>

                    <div className="card-actions">
                      {app.status === 'Pending' && canManage && (
                        <>
                          <button
                            className="mgmt-btn btn-approve"
                            onClick={() => handleOpenApprovalModal(app)}
                          >
                            <FaCheckCircle /> Onayla
                          </button>
                          <button
                            className="mgmt-btn btn-reject"
                            onClick={async () => {
                              Modal.confirm({
                                title: 'Başvuruyu Reddet ve Sil',
                                content: `${app.firstName} ${app.lastName} kulüp başvurusunu reddetmek ve sistemden silmek istediğinize emin misiniz? Bu işlem kullanıcının tekrar başvurabilmesini sağlar.`,
                                okText: 'Evet, Reddet ve Sil',
                                cancelText: 'İptal',
                                okType: 'danger',
                                onOk: async () => {
                                  try {
                                    const response = await deleteClubApplication(app.id);
                                    if (response?.isSuccess === false) {
                                      showToast(response?.resultMessage || 'Başvuru silinemedi.', 'error');
                                      return;
                                    }
                                    showToast('Başvuru reddedildi ve sistemden kaldırıldı.', 'success');
                                    fetchClubApplications();
                                  } catch (err) {
                                    console.error('Silme hatası:', err);
                                    showToast('İşlem sırasında bir hata oluştu.', 'error');
                                  }
                                }
                              });
                            }}
                          >
                            <FaTrashAlt /> Reddet
                          </button>
                        </>
                      )}

                      {app.status === 'Approved' && (
                        <>
                          <button
                            className="mgmt-btn btn-assign"
                            onClick={() => handleOpenSlotAssignModal(app)}
                          >
                            <FaClock /> Saat Ata
                          </button>
                          <button
                            className="mgmt-btn"
                            style={{ background: '#1cc88a', color: 'white' }}
                            onClick={() => handleOpenPaymentModal(app)}
                          >
                            <FaMoneyBillWave /> Ödemeler
                          </button>
                          {app.isExpired && (
                            <button
                              className="mgmt-btn btn-renew"
                              onClick={() => handleOpenRenewalModal(app)}
                            >
                              <FaCalendarAlt /> Yenile
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Club Approval Modal ── */}
      {showApprovalModal && approvalApp && (
        <div className="appointment-modal-overlay" onClick={() => setShowApprovalModal(false)}>
          <div className="appointment-modal club-mgmt-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1cc88a, #17a673)', color: 'white' }}>
              <h3>Kulüp Onaylama</h3>
              <button className="modal-close" onClick={() => setShowApprovalModal(false)} style={{ color: 'white' }}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ width: '60px', height: '60px', background: '#f0fff4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', border: '2px solid #c6f6d5' }}>
                  <FaFutbol style={{ fontSize: '2rem', color: '#1cc88a' }} />
                </div>
                <h4 style={{ margin: 0, color: '#2e384d', fontSize: '1.2rem' }}>{approvalApp.firstName} {approvalApp.lastName}</h4>
                <p style={{ color: '#858796', marginTop: '0.25rem' }}>Kulüp üyeliğini başlatmak üzeresiniz.</p>
              </div>

              <div className="form-group">
                <label style={{ color: '#4e73df', fontWeight: 800, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Üyelik Süresi Seçiniz</label>
                <div className="membership-selector">
                  {[1, 3, 6, 12].map(m => (
                    <button
                      key={m}
                      type="button"
                      className={`month-btn ${approvalMembershipMonths === m ? 'selected' : ''}`}
                      onClick={() => setApprovalMembershipMonths(m)}
                    >
                      {m} Ay
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-actions" style={{ padding: '1.5rem', background: '#f8f9fc' }}>
              <button
                className="mgmt-btn"
                style={{ background: '#858796', color: 'white', border: 'none', padding: '0.75rem 1.5rem' }}
                onClick={() => setShowApprovalModal(false)}
              >
                İptal
              </button>
              <button
                className="mgmt-btn btn-approve"
                style={{ padding: '0.75rem 2rem' }}
                onClick={() => handleApproveMembership(approvalApp.id, approvalMembershipMonths)}
              >
                <FaCheckCircle /> Onayla ({approvalMembershipMonths} Ay)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Club Renewal Modal ── */}
      {showRenewalModal && renewalApp && (
        <div className="appointment-modal-overlay" onClick={() => setShowRenewalModal(false)}>
          <div className="appointment-modal club-mgmt-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #f6c23e, #f4b619)', color: '#333' }}>
              <h3>Üyelik Yenileme</h3>
              <button className="modal-close" onClick={() => setShowRenewalModal(false)} style={{ color: '#333' }}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ width: '60px', height: '60px', background: '#fffcf0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', border: '2px solid #ffeeba' }}>
                  <FaClock style={{ fontSize: '2rem', color: '#f6c23e' }} />
                </div>
                <h4 style={{ margin: 0, color: '#2e384d', fontSize: '1.2rem' }}>{renewalApp.firstName} {renewalApp.lastName}</h4>
                <p style={{ color: '#858796', marginTop: '0.25rem' }}>Üyelik süresini uzatmak üzeresiniz.</p>
              </div>

              <div className="form-group">
                <label style={{ color: '#856404', fontWeight: 800, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Eklenecek Süre</label>
                <div className="membership-selector">
                  {[1, 3, 6, 12].map(m => (
                    <button
                      key={m}
                      type="button"
                      className={`month-btn ${renewalMonths === m ? 'renewal-selected' : ''}`}
                      onClick={() => setRenewalMonths(m)}
                    >
                      +{m} Ay
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-actions" style={{ padding: '1.5rem', background: '#f8f9fc' }}>
              <button
                className="mgmt-btn"
                style={{ background: '#858796', color: 'white', border: 'none', padding: '0.75rem 1.5rem' }}
                onClick={() => setShowRenewalModal(false)}
              >
                İptal
              </button>
              <button
                className="mgmt-btn btn-renew"
                style={{ padding: '0.75rem 2rem' }}
                onClick={handleRenewMembership}
              >
                <FaCalendarAlt /> Süreyi Uzat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Club Time Slot Assignment Modal - Professionalized ── */}
      {showSlotAssignModal && slotAssignApp && (
        <div className="appointment-modal-overlay" onClick={() => setShowSlotAssignModal(false)}>
          <div className="appointment-modal club-mgmt-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '750px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #4e73df, #224abe)', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '8px', borderRadius: '10px' }}>
                  <FaClock style={{ fontSize: '1.2rem' }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Saat Ataması</h3>
                  <small style={{ color: 'rgba(255,255,255,0.8)' }}>{slotAssignApp.firstName} {slotAssignApp.lastName}</small>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowSlotAssignModal(false)} style={{ color: 'white' }}>×</button>
            </div>

            <div className="modal-body" style={{ maxHeight: '70vh', background: '#fbfcfe', overflowY: 'auto' }}>
              {/* Existing Assignments Section */}
              <div className="assignment-table-container">
                <h4>MEVCUT ATAMALAR ({clubTimeSlots.length})</h4>
                {clubTimeSlots.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '1.5rem', color: '#858796', fontStyle: 'italic', background: 'white', borderRadius: '10px' }}>
                    Henüz atanmış bir saat bulunmamaktadır.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="assignment-table">
                      <thead>
                        <tr>
                          <th>TESİS</th>
                          <th>GÜN</th>
                          <th>SAAT</th>
                          <th>ÖDEME DURUMU</th>
                          <th style={{ textAlign: 'center' }}>İŞLEM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clubTimeSlots.map(slot => {
                          const slotPaid = slot.isPaid || (slot.paidUntilDate && new Date() <= new Date(slot.paidUntilDate));
                          return (
                            <tr key={slot.assignmentId}>
                              <td style={{ fontWeight: 600 }}>{slot.facilityName}</td>
                              <td>{getDayName(slot.dayOfWeek)}</td>
                              <td>
                                <span style={{ background: '#eef2ff', color: '#4338ca', padding: '2px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '0.8rem' }}>
                                  {slot.startTime} - {slot.endTime}
                                </span>
                              </td>
                              <td style={{ padding: '0.8rem 1rem', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditSlotId(slot.id);
                                    setEditSlotData({
                                      dayOfWeek: slot.dayOfWeek,
                                      startTime: slot.startTime,
                                      endTime: slot.endTime,
                                      facilityId: Number(slot.facilityId || slot.FacilityId || slot.sportsFacilityId || 0)
                                    });
                                  }}
                                  style={{
                                    background: '#f6c23e',
                                    color: 'white',
                                    border: 'none',
                                    padding: '5px 10px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  DÜZENLE
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    Modal.confirm({
                                      title: 'Atamayı Kaldır',
                                      content: `${slot.facilityName} tesisindeki bu saat atamasını kaldırmak istediğinize emin misiniz?`,
                                      okText: 'Evet, Kaldır',
                                      cancelText: 'İptal',
                                      okType: 'danger',
                                      onOk: () => handleRemoveTimeSlot(slot.assignmentId)
                                    });
                                  }}
                                  style={{
                                    background: '#e74a3b',
                                    color: 'white',
                                    border: 'none',
                                    padding: '5px 10px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  KALDIR
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="new-assignment-form" style={{ marginTop: '1rem' }}>
                <h4>TEKİL SEANS YÖNETİMİ</h4>
                <div style={{ display: 'grid', gap: '0.9rem' }}>
                  {clubTimeSlots.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1rem 1.25rem', color: '#94a3b8', background: 'white', border: '1px dashed #cbd5e1', borderRadius: '10px' }}>
                      Tekil ekleme veya çıkarma yapabilmek için önce saat ataması bulunmalıdır.
                    </div>
                  ) : (
                    clubTimeSlots.map((slot) => {
                      const slotPaid = slot.isPaid || (slot.paidUntilDate && new Date() <= new Date(slot.paidUntilDate));
                      const cancelledSessions = Array.isArray(slot.cancelledSessions) ? slot.cancelledSessions : [];
                      const extraSessions = Array.isArray(slot.extraSessions) ? slot.extraSessions : [];
                      const membershipStart = slotAssignApp?.membershipStartDate ? new Date(slotAssignApp.membershipStartDate) : null;
                      const membershipEnd = slotAssignApp?.membershipEndDate ? new Date(slotAssignApp.membershipEndDate) : null;

                      return (
                        <div
                          key={`session-actions-${slot.assignmentId}`}
                          style={{
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '1rem',
                            boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.95rem' }}>
                                {slot.facilityName}
                              </div>
                              <div style={{ marginTop: '0.35rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                <span style={{ background: '#eef2ff', color: '#4338ca', padding: '4px 10px', borderRadius: '999px', fontWeight: 700, fontSize: '0.74rem' }}>
                                  {getDayName(slot.dayOfWeek)} • {slot.startTime} - {slot.endTime}
                                </span>
                                {membershipStart && membershipEnd && (
                                  <span style={{ background: '#f8fafc', color: '#475569', padding: '4px 10px', borderRadius: '999px', fontWeight: 700, fontSize: '0.74rem' }}>
                                    Üyelik: {membershipStart.toLocaleDateString('tr-TR')} - {membershipEnd.toLocaleDateString('tr-TR')}
                                  </span>
                                )}
                                <span style={{
                                  background: slotPaid ? '#ecfdf3' : '#fff7ed',
                                  color: slotPaid ? '#027a48' : '#c2410c',
                                  padding: '4px 10px',
                                  borderRadius: '999px',
                                  fontWeight: 700,
                                  fontSize: '0.74rem'
                                }}>
                                  {slotPaid ? 'Odeme aktif' : 'Odeme bekliyor'}
                                </span>
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => openClubSessionActionModal(slot, 'remove')}
                                style={{
                                  background: '#fff7ed',
                                  color: '#c2410c',
                                  border: '1px solid #fdba74',
                                  borderRadius: '8px',
                                  padding: '0.55rem 0.9rem',
                                  fontSize: '0.78rem',
                                  fontWeight: 800,
                                  cursor: 'pointer'
                                }}
                              >
                                Tekil Saat Çıkar
                              </button>
                              <button
                                type="button"
                                onClick={() => openClubSessionActionModal(slot, 'extra')}
                                style={{
                                  background: '#eff6ff',
                                  color: '#1d4ed8',
                                  border: '1px solid #93c5fd',
                                  borderRadius: '8px',
                                  padding: '0.55rem 0.9rem',
                                  fontSize: '0.78rem',
                                  fontWeight: 800,
                                  cursor: 'pointer'
                                }}
                              >
                                Ek Saat Ekle
                              </button>
                            </div>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', marginTop: '0.9rem' }}>
                            <div style={{ border: '1px solid #fed7aa', background: '#fffaf5', borderRadius: '10px', padding: '0.85rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <strong style={{ color: '#9a3412', fontSize: '0.82rem' }}>Çıkarılan Tekil Seanslar</strong>
                                <span style={{ color: '#9ca3af', fontSize: '0.74rem' }}>{cancelledSessions.length} kayıt</span>
                              </div>

                              {cancelledSessions.length === 0 ? (
                                <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Kayıt yok.</div>
                              ) : (
                                <div style={{ display: 'grid', gap: '0.55rem' }}>
                                  {cancelledSessions.map((cancellation) => (
                                    <div
                                      key={cancellation.id}
                                      style={{
                                        background: 'white',
                                        border: '1px solid #fdba74',
                                        borderRadius: '8px',
                                        padding: '0.7rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: '0.75rem'
                                      }}
                                    >
                                      <div>
                                        <div style={{ fontWeight: 700, color: '#9a3412', fontSize: '0.8rem' }}>
                                          {new Date(cancellation.sessionDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                                        </div>
                                        {cancellation.reason && (
                                          <div style={{ marginTop: '0.25rem', color: '#7c2d12', fontSize: '0.74rem' }}>
                                            {cancellation.reason}
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteClubSessionAction('remove', cancellation.id)}
                                        style={{
                                          background: 'transparent',
                                          border: 'none',
                                          color: '#dc2626',
                                          fontWeight: 700,
                                          cursor: 'pointer',
                                          fontSize: '0.74rem'
                                        }}
                                      >
                                        Kaldır
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div style={{ border: '1px solid #bfdbfe', background: '#f8fbff', borderRadius: '10px', padding: '0.85rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <strong style={{ color: '#1d4ed8', fontSize: '0.82rem' }}>Ek Seanslar</strong>
                                <span style={{ color: '#9ca3af', fontSize: '0.74rem' }}>{extraSessions.length} kayıt</span>
                              </div>

                              {extraSessions.length === 0 ? (
                                <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Kayıt yok.</div>
                              ) : (
                                <div style={{ display: 'grid', gap: '0.55rem' }}>
                                  {extraSessions.map((extraSession) => (
                                    <div
                                      key={extraSession.id}
                                      style={{
                                        background: 'white',
                                        border: '1px solid #93c5fd',
                                        borderRadius: '8px',
                                        padding: '0.7rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: '0.75rem'
                                      }}
                                    >
                                      <div>
                                        <div style={{ fontWeight: 700, color: '#1d4ed8', fontSize: '0.8rem' }}>
                                          {new Date(extraSession.sessionDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                                        </div>
                                        <div style={{ marginTop: '0.2rem', color: '#1e40af', fontSize: '0.74rem' }}>
                                          {extraSession.startTime} - {extraSession.endTime}
                                          {extraSession.laneCount ? ` • ${extraSession.laneCount} kulvar` : ''}
                                        </div>
                                        {extraSession.notes && (
                                          <div style={{ marginTop: '0.25rem', color: '#1e3a8a', fontSize: '0.74rem' }}>
                                            {extraSession.notes}
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteClubSessionAction('extra', extraSession.id)}
                                        style={{
                                          background: 'transparent',
                                          border: 'none',
                                          color: '#dc2626',
                                          fontWeight: 700,
                                          cursor: 'pointer',
                                          fontSize: '0.74rem'
                                        }}
                                      >
                                        Kaldır
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* New Assignment Section */}
              <div className="new-assignment-form">
                <h4>YENİ SAAT ATAMASI</h4>

                  {slotAssignApp.isExpired && (
                    <div className="expired-warning">
                      <FaTimesCircle />
                      Üyelik süresi dolduğu için yeni saat ataması yapılamaz. Lütfen önce üyeliği yenileyin.
                    </div>
                  )}

                  <div className="form-grid">
                    <div className="form-group">
                      <label style={{ display: 'block', fontWeight: 800, color: '#4e73df', fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Tesis Seçimi</label>
                      {(() => {
                        const appFacId = Number(slotAssignApp?.facilityId || slotAssignApp?.FacilityId || slotAssignApp?.sportsFacilityId || 0);
                        const allFacilities = (regions || []).flatMap(r => r.facilities || []);
                        const isValidFacility = appFacId > 0 && allFacilities.some(f => (f.id || f.facilityId) === appFacId);

                        return !isValidFacility ? (
                          <select
                            value={slotAssignData.facilityId || ''}
                            onChange={(e) => setSlotAssignData(prev => ({ ...prev, facilityId: e.target.value }))}
                            className="form-control"
                            style={{ width: '100%', background: 'white', border: '1px solid #d1d3e2', color: '#5a5c69', padding: '0.6rem', borderRadius: '5px' }}
                          >
                            <option value="">-- Tesis Seçiniz --</option>
                            {allFacilities.map(f => {
                              const facId = f.id || f.facilityId;
                              return <option key={facId} value={facId}>{f.name || f.facilityName}</option>;
                            })}
                          </select>
                        ) : (
                          <div style={{ background: '#f8f9fc', border: '1px solid #e3e6f0', padding: '0.6rem', borderRadius: '8px', color: '#2e384d', fontWeight: 700, fontSize: '0.9rem' }}>
                            {slotAssignApp.facilityName || 'Tesis Belirtilmemiş'}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'block', fontWeight: 800, color: '#4e73df', fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Gün Seçimi</label>
                      <select
                        value={slotAssignData.dayOfWeek}
                        onChange={(e) => setSlotAssignData(prev => ({ ...prev, dayOfWeek: e.target.value }))}
                        className="form-control"
                        style={{ width: '100%', background: 'white', border: '1px solid #d1d3e2', color: '#5a5c69', padding: '0.6rem', borderRadius: '5px' }}
                      >
                        {[1, 2, 3, 4, 5, 6, 0].map(d => (
                          <option key={d} value={d}>{getDayName(d)}</option>
                        ))}
                      </select>
                    </div>

                    {/* Tarih Referansı Seçimi */}
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontWeight: 800, color: '#4e73df', fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Tarih Referansı</label>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <label style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '10px 16px', borderRadius: '8px', cursor: 'pointer',
                          border: slotAssignData.dateReference === 'today' ? '2px solid #4e73df' : '2px solid #e3e6f0',
                          background: slotAssignData.dateReference === 'today' ? '#eef2ff' : '#fff',
                          fontWeight: 600, fontSize: '0.85rem', color: slotAssignData.dateReference === 'today' ? '#4e73df' : '#5a5c69',
                          transition: 'all 0.2s'
                        }}>
                          <input type="radio" name="dateRef" value="today" checked={slotAssignData.dateReference === 'today'}
                            onChange={() => setSlotAssignData(prev => ({ ...prev, dateReference: 'today' }))} />
                          Bugünden itibaren ata
                        </label>
                        <label style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '10px 16px', borderRadius: '8px', cursor: 'pointer',
                          border: slotAssignData.dateReference === 'membershipStart' ? '2px solid #f6c23e' : '2px solid #e3e6f0',
                          background: slotAssignData.dateReference === 'membershipStart' ? '#fffcf0' : '#fff',
                          fontWeight: 600, fontSize: '0.85rem', color: slotAssignData.dateReference === 'membershipStart' ? '#856404' : '#5a5c69',
                          transition: 'all 0.2s'
                        }}>
                          <input type="radio" name="dateRef" value="membershipStart" checked={slotAssignData.dateReference === 'membershipStart'}
                            onChange={() => setSlotAssignData(prev => ({ ...prev, dateReference: 'membershipStart' }))} />
                          Üyelik başlangıcından itibaren ata
                        </label>
                      </div>
                      {slotAssignData.dateReference === 'today' && (
                        <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#6c757d', fontStyle: 'italic' }}>
                          💡 Seanslar bugünden itibaren hesaplanır. Geçmiş tarihlerdeki günler dahil edilmez.
                        </p>
                      )}
                      {slotAssignData.dateReference === 'membershipStart' && (
                        <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#856404', fontStyle: 'italic' }}>
                          ⚠️ Seanslar üyelik başlangıç tarihinden itibaren hesaplanır. Geçmiş günler de dahil edilir.
                        </p>
                      )}
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'block', fontWeight: 800, color: '#4e73df', fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Başlangıç Saati</label>
                      <input
                        type="time"
                        step="1800"
                        value={slotAssignData.startTime}
                        onChange={(e) => setSlotAssignData(prev => ({ ...prev, startTime: e.target.value }))}
                        className="form-control"
                        style={{ width: '100%', background: 'white', border: '1px solid #d1d3e2', color: '#5a5c69', padding: '0.6rem', borderRadius: '5px' }}
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'block', fontWeight: 800, color: '#4e73df', fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Bitiş Saati</label>
                      <input
                        type="time"
                        step="1800"
                        value={slotAssignData.endTime}
                        onChange={(e) => setSlotAssignData(prev => ({ ...prev, endTime: e.target.value }))}
                        className="form-control"
                        style={{ width: '100%', background: 'white', border: '1px solid #d1d3e2', color: '#5a5c69', padding: '0.6rem', borderRadius: '5px' }}
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <button
                      type="button"
                      disabled={slotAssignApp.isExpired}
                      onClick={handleAssignTimeSlot}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#4e73df',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '700',
                        cursor: slotAssignApp.isExpired ? 'not-allowed' : 'pointer',
                        opacity: slotAssignApp.isExpired ? 0.6 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s',
                        boxShadow: '0 4px 6px rgba(78, 115, 223, 0.2)'
                      }}
                    >
                      <FaClock /> SAATİ ATA
                    </button>
              </div>
            </div>
          </div>
          <div className="modal-actions" style={{ background: '#f8f9fc', borderTop: '1px solid #e3e6f0' }}>
            <button
              className="mgmt-btn"
              style={{ background: '#858796', color: 'white', border: 'none', padding: '0.6rem 2rem' }}
              onClick={() => setShowSlotAssignModal(false)}
            >
              KAPAT
            </button>
          </div>
        </div>
      </div>
    )}

      {/* ── Club Payment Modal ── */}
      {clubSessionActionModalVisible && clubSessionActionAssignment && (
        <div className="appointment-modal-overlay" onClick={() => setClubSessionActionModalVisible(false)}>
          <div
            className="appointment-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header" style={{ background: clubSessionActionType === 'extra' ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : 'linear-gradient(135deg, #ea580c, #c2410c)' }}>
              <h3 style={{ color: 'white' }}>
                {clubSessionActionType === 'extra' ? 'Ek Seans Ekle' : 'Tekil Saat Çıkar'}
              </h3>
              <button className="modal-close" onClick={() => setClubSessionActionModalVisible(false)} style={{ color: 'white' }}>×</button>
            </div>

            <div className="modal-body" style={{ padding: '1.25rem', background: '#fbfcfe' }}>
              <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ fontWeight: 800, color: '#1e293b', marginBottom: '0.35rem' }}>
                  {clubSessionActionAssignment.facilityName}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span style={{ background: '#eef2ff', color: '#4338ca', padding: '4px 10px', borderRadius: '999px', fontSize: '0.74rem', fontWeight: 700 }}>
                    {getDayName(clubSessionActionAssignment.dayOfWeek)} • {clubSessionActionAssignment.startTime} - {clubSessionActionAssignment.endTime}
                  </span>
                  {slotAssignApp?.membershipStartDate && slotAssignApp?.membershipEndDate && (
                    <span style={{ background: '#f8fafc', color: '#475569', padding: '4px 10px', borderRadius: '999px', fontSize: '0.74rem', fontWeight: 700 }}>
                      Üyelik: {new Date(slotAssignApp.membershipStartDate).toLocaleDateString('tr-TR')} - {new Date(slotAssignApp.membershipEndDate).toLocaleDateString('tr-TR')}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 800, color: '#4e73df', fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                    Tarih
                  </label>
                  <DatePicker
                    value={clubSessionActionDate}
                    onChange={(value) => setClubSessionActionDate(value)}
                    format="DD.MM.YYYY"
                    style={{ width: '100%', height: '42px' }}
                    disabledDate={(current) => {
                      if (!current) return false;
                      if (slotAssignApp?.membershipStartDate && current.isBefore(dayjs(slotAssignApp.membershipStartDate).startOf('day'))) {
                        return true;
                      }
                      if (slotAssignApp?.membershipEndDate && current.isAfter(dayjs(slotAssignApp.membershipEndDate).endOf('day'))) {
                        return true;
                      }
                      return false;
                    }}
                  />
                  <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#64748b' }}>
                    {clubSessionActionType === 'extra'
                      ? 'Bu tarih için mevcut programa ek tekil seans tanımlanır.'
                      : 'Bu tarih için tekrarlayan seans yalnızca bir defaya mahsus çıkarılır.'}
                  </div>
                </div>

                {clubSessionActionType === 'extra' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 800, color: '#4e73df', fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Başlangıç Saati
                      </label>
                      <input
                        type="time"
                        step="1800"
                        value={clubSessionActionStartTime}
                        onChange={(e) => setClubSessionActionStartTime(e.target.value)}
                        className="form-control"
                        style={{ width: '100%', background: 'white', border: '1px solid #d1d3e2', color: '#5a5c69', padding: '0.6rem', borderRadius: '5px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 800, color: '#4e73df', fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Bitiş Saati
                      </label>
                      <input
                        type="time"
                        step="1800"
                        value={clubSessionActionEndTime}
                        onChange={(e) => setClubSessionActionEndTime(e.target.value)}
                        className="form-control"
                        style={{ width: '100%', background: 'white', border: '1px solid #d1d3e2', color: '#5a5c69', padding: '0.6rem', borderRadius: '5px' }}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', fontWeight: 800, color: '#4e73df', fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                    {clubSessionActionType === 'extra' ? 'Not' : 'Çıkarma Nedeni'}
                  </label>
                  <textarea
                    value={clubSessionActionNotes}
                    onChange={(e) => setClubSessionActionNotes(e.target.value)}
                    rows={3}
                    className="form-control"
                    placeholder={clubSessionActionType === 'extra' ? 'İsteğe bağlı not' : 'İsteğe bağlı neden'}
                    style={{ width: '100%', background: 'white', border: '1px solid #d1d3e2', color: '#5a5c69', padding: '0.75rem', borderRadius: '8px', resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-actions" style={{ background: '#f8f9fc', borderTop: '1px solid #e3e6f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                className="mgmt-btn"
                style={{ background: '#858796', color: 'white', border: 'none', padding: '0.6rem 1.4rem' }}
                onClick={() => setClubSessionActionModalVisible(false)}
                disabled={clubSessionActionLoading}
              >
                Vazgeç
              </button>
              <button
                className="mgmt-btn"
                style={{
                  background: clubSessionActionType === 'extra' ? '#2563eb' : '#ea580c',
                  color: 'white',
                  border: 'none',
                  padding: '0.6rem 1.4rem',
                  opacity: clubSessionActionLoading ? 0.7 : 1
                }}
                onClick={handleSubmitClubSessionAction}
                disabled={clubSessionActionLoading}
              >
                {clubSessionActionLoading
                  ? 'Kaydediliyor...'
                  : clubSessionActionType === 'extra'
                    ? 'Ek Seansı Kaydet'
                    : 'Çıkarma Kaydını Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && paymentModalApp && (
        <div className="appointment-modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="appointment-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h3>Ödemeler – {paymentModalApp.firstName} {paymentModalApp.lastName}</h3>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              {paymentModalLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div className="loading-spinner"></div>
                  <p style={{ color: '#858796', marginTop: '1rem' }}>Ödeme bilgileri yükleniyor...</p>
                </div>
              ) : !paymentModalData ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#858796' }}>
                  <FaMoneyBillWave style={{ fontSize: '3rem', marginBottom: '1rem', color: '#d1d3e2' }} />
                  <p>Bu kulübe ait ödeme bilgisi bulunamadı.</p>
                </div>
              ) : (() => {
                const info = paymentModalData;
                const normalizeDate = (value) => {
                  const date = new Date(value);
                  date.setHours(0, 0, 0, 0);
                  return date;
                };

const getSlotPeriod = (slot) => {
                  const today = normalizeDate(new Date());
                  const region = info.regionBreakdowns?.find(r => r.clubApplicationId === slot.clubApplicationId);
                  const membershipStart = region?.membershipStartDate
                    ? normalizeDate(region.membershipStartDate)
                    : (info.membershipStartDate ? normalizeDate(info.membershipStartDate) : new Date(today.getFullYear(), today.getMonth(), 1));
                  const membershipEnd = region?.membershipEndDate
                    ? normalizeDate(region.membershipEndDate)
                    : (info.membershipEndDate ? normalizeDate(info.membershipEndDate) : new Date(today.getFullYear(), today.getMonth() + 1, 0));
                  const assignmentStart = slot?.createdAt
                    ? normalizeDate(slot.createdAt)
                    : membershipStart;

                  return {
                    start: assignmentStart > membershipStart ? assignmentStart : membershipStart,
                    end: membershipEnd
                  };
                };

                const isTruthyPaidValue = (value) => {
                  if (value === true || value === 1) return true;
                  if (typeof value === 'string') {
                    const normalized = value.toLowerCase();
                    return ['true', '1', 'paid', 'success', 'approved', 'sale', 'auth'].includes(normalized);
                  }
                  return false;
                };

                const hasPaidMarker = (item) => {
                  if (!item) return false;
                  const status = (item.paymentStatus || item.PaymentStatus || item.status || item.Status || '').toString().toLowerCase();
                  if (['failed', 'declined', 'error', 'cancelled', 'canceled', 'refunded'].includes(status)) return false;
                  return isTruthyPaidValue(item.isPaid ?? item.IsPaid)
                    || isTruthyPaidValue(item.paid ?? item.Paid)
                    || isTruthyPaidValue(item.isPaymentCompleted ?? item.IsPaymentCompleted)
                    || ['paid', 'success', 'approved', 'sale', 'auth'].includes(status)
                    || !!(item.paidAt || item.PaidAt || item.paymentDate || item.PaymentDate)
                    || !!(item.paymentTransactionId || item.PaymentTransactionId || item.transactionId || item.TransactionId);
                };

                const timePart = (value) => (value || '').toString().substring(0, 5);

                const getId = (item, ...keys) => {
                  for (const key of keys) {
                    if (item?.[key] !== undefined && item?.[key] !== null) return item[key].toString();
                  }
                  return '';
                };

                // Expand assignments into individual sessions
                const getExpandedSessions = () => {
                  if (!info.assignments) return [];
                  const sessions = [];
(info.assignments || []).forEach(slot => {
                    // Tesis bazlı üyelik süresini kendi region üzerinden al
                    const { start: slotMStart, end: slotMEnd } = getSlotPeriod(slot);

                    let current = new Date(slotMStart);
                    while (current <= slotMEnd) {
                      if (current.getDay() === slot.dayOfWeek) {
                        const sessionDate = new Date(current);
                        const sessionKey = dayjs(current).format('YYYY-MM-DD');
                        const isCancelledDate = !!(slot.cancelledSessions || []).some(cancellation => {
                          const cancelledDateStr = dayjs(cancellation.sessionDate || cancellation.SessionDate).format('YYYY-MM-DD');
                          return cancelledDateStr === sessionKey;
                        });
                        const sessionPayment = slot.sessionPayments && slot.sessionPayments.find(p => {
                          const pDateStr = dayjs(p.sessionDate || p.SessionDate).format('YYYY-MM-DD');
                          return pDateStr === sessionKey;
                        });
                        const paidUntil = slot.paidUntilDate ? new Date(slot.paidUntilDate) : null;
                        const sessionIsPaid = slot.isPaid || (paidUntil && sessionDate <= paidUntil) || !!sessionPayment;

                        // Get transfer info if this date is a source
                        const transferInfo = slot.transfers && slot.transfers.find(t => {
                          const sDateStr = dayjs(t.originalSessionDate || t.OriginalSessionDate).format('YYYY-MM-DD');
                          return sDateStr === sessionKey;
                        });

                        const isSourceDate = !!transferInfo;
                        const isTargetDate = slot.transfers && slot.transfers.some(t => {
                          const tDateStr = dayjs(t.newSessionDate || t.NewSessionDate).format('YYYY-MM-DD');
                          return tDateStr === sessionKey;
                        });

                        if (!isTargetDate && !isCancelledDate) {
                          sessions.push({
                            ...slot,
                            sessionDate,
                            sessionKey,
                            dateDisplay: current.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }),
                            isPaid: !!sessionIsPaid,
                            isTransferred: isSourceDate || (sessionPayment ? !!sessionPayment.isTransferred : false),
                            transferTargetDate: transferInfo ? (transferInfo.newSessionDate || transferInfo.NewSessionDate) : null,
                            transferTargetStartTime: transferInfo ? (transferInfo.newStartTime || transferInfo.NewStartTime) : null,
                            transferTargetEndTime: transferInfo ? (transferInfo.newEndTime || transferInfo.NewEndTime) : null,
                            laneCount: sessionPayment ? sessionPayment.laneCount : slot.laneCount
                          });
                        }
                      }
                      current.setDate(current.getDate() + 1);
                    }
                    
                    if (slot.transfers && slot.transfers.length > 0) {
                      slot.transfers.forEach(transfer => {
                        const targetDate = new Date(transfer.newSessionDate || transfer.NewSessionDate);
                        sessions.push({
                          ...slot,
                          sessionDate: targetDate,
                          dateDisplay: targetDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }),
                          dayOfWeek: targetDate.getDay(),
                          startTime: transfer.newStartTime || transfer.NewStartTime || slot.startTime,
                          endTime: transfer.newEndTime || transfer.NewEndTime || slot.endTime,
                          isPaid: true,
                          isTransferred: false,
                          isTransferTarget: true,
                          laneCount: slot.laneCount
                        });
                      });
                    }

                    if (slot.extraSessions && slot.extraSessions.length > 0) {
                      slot.extraSessions.forEach(extraSession => {
                        const extraDate = new Date(extraSession.sessionDate || extraSession.SessionDate);
                        const extraSessionKey = dayjs(extraDate).format('YYYY-MM-DD');
                        const extraSessionPayment = slot.sessionPayments && slot.sessionPayments.find(p => {
                          const paymentExtraSessionId = getId(p, 'extraSessionId', 'ExtraSessionId', 'clubExtraSessionId', 'ClubExtraSessionId');
                          const currentExtraSessionId = getId(extraSession, 'id', 'Id', 'extraSessionId', 'ExtraSessionId');
                          if (paymentExtraSessionId && currentExtraSessionId && paymentExtraSessionId === currentExtraSessionId) {
                            return !p.isTransferred && !p.IsTransferred;
                          }

                          const pDateStr = dayjs(p.sessionDate || p.SessionDate).format('YYYY-MM-DD');
                          const sameDate = pDateStr === extraSessionKey;
                          const sameStart = !p.startTime && !p.StartTime
                            ? true
                            : timePart(p.startTime || p.StartTime) === timePart(extraSession.startTime || extraSession.StartTime || slot.startTime);
                          const sameEnd = !p.endTime && !p.EndTime
                            ? true
                            : timePart(p.endTime || p.EndTime) === timePart(extraSession.endTime || extraSession.EndTime || slot.endTime);
                          return sameDate && sameStart && sameEnd && !p.isTransferred && !p.IsTransferred;
                        });
                        const extraSessionIsPaid = hasPaidMarker(extraSession) || hasPaidMarker(extraSessionPayment);
                        sessions.push({
                          ...slot,
                          sessionDate: extraDate,
                          sessionKey: extraSessionKey,
                          dateDisplay: extraDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }),
                          dayOfWeek: extraDate.getDay(),
                          startTime: extraSession.startTime || extraSession.StartTime || slot.startTime,
                          endTime: extraSession.endTime || extraSession.EndTime || slot.endTime,
                          isPaid: extraSessionIsPaid,
                          isTransferred: false,
                          isTransferTarget: false,
                          isExtraSession: true,
                          laneCount: extraSessionPayment ? extraSessionPayment.laneCount : (extraSession.laneCount ?? extraSession.LaneCount ?? slot.laneCount)
                        });
                      });
                    }
                  });
                  return sessions.sort((a, b) => a.sessionDate - b.sessionDate);
                };

                const allSessions = getExpandedSessions();

                // Group by facility
                const facilityGroups = {};
                (info.assignments || []).forEach(slot => {
                  const key = slot.facilityName || 'Bilinmeyen Tesis';
                  if (!facilityGroups[key]) facilityGroups[key] = [];
                  facilityGroups[key].push(slot);
                });

                const sessionsByFacility = {};
                allSessions.forEach(session => {
                  const fName = session.facilityName || 'Bilinmeyen Tesis';
                  if (!sessionsByFacility[fName]) sessionsByFacility[fName] = [];
                  sessionsByFacility[fName].push(session);
                });

                let grandTotal = 0;
                let grandPaid = 0;

                return (
                  <div>
                    {/* Club Info */}
                    <div style={{
                      background: '#f8f9fc',
                      borderRadius: '10px',
                      padding: '1rem',
                      marginBottom: '1rem',
                      border: '1px solid #e3e6f0'
                    }}>
                      <h4 style={{ margin: '0 0 0.5rem', color: '#5a5c69' }}>{info.clubName}</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
                        <div><strong>Vergi No:</strong> {info.taxNumber}</div>
                        <div><strong>Kayıtlı Tesis Sayısı:</strong> {info.regionBreakdowns?.length || 0}</div>
                      </div>
                    </div>

                    {/* Facility Cards */}
                    {Object.entries(facilityGroups).length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#858796' }}>
                        Bu kulübe atanmış saat bulunamadı.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {Object.entries(facilityGroups).map(([facilityName, slots], fIdx) => {
                          const activeTab = paymentFacilityTab[facilityName] || 'unpaid';
                          const rawFacilitySessions = sessionsByFacility[facilityName] || [];

                          // Augment sessions with effective lane count and price
                          const facilitySessions = rawFacilitySessions.map(session => {
                            let effectiveLaneCount = session.laneCount || 1;

                            // For old paid pool sessions where laneCount is null or 1, get it from the matched transaction
                            if ((session.laneCount == null || session.laneCount === 1) && session.isPaid && session.lanePricePerHour > 0) {
                              const matchedTx = paymentTransactions.find(tx => {
                                if (tx.paymentMode !== 'daily' && tx.paymentMode !== 'full') return false;
                                const txDate = new Date(tx.createdAt);
                                return txDate.getDay() === session.dayOfWeek;
                              });

                              if (matchedTx) {
                                const coveredSessionsCount = rawFacilitySessions.filter(s =>
                                  s.isPaid && s.dayOfWeek === session.dayOfWeek
                                ).length || 1;

                                const laneCalc = Math.round(matchedTx.amount / (coveredSessionsCount * session.lanePricePerHour));
                                if (laneCalc > 1 && laneCalc <= 10) {
                                  effectiveLaneCount = laneCalc;
                                } else if (matchedTx.laneCount > 1) {
                                  effectiveLaneCount = matchedTx.laneCount;
                                }
                              }
                            }

                            // Gündüz/gece ayrımı yapan tek seans fiyatını hesapla (public site ile aynı mantık)
                            const getSlotPrice = (s) => {
                              const parseTimeToHours = (timeStr) => {
                                if (!timeStr) return 0;
                                const parts = timeStr.split(':');
                                return parseInt(parts[0], 10) + ((parts.length > 1 ? parseInt(parts[1], 10) : 0) / 60);
                              };
                              const startH = parseTimeToHours(s.startTime);
                              let endH = parseTimeToHours(s.endTime);
                              if (endH === 0) endH = 24.0;

                              let dayH = 0, nightH = 0;
                              const nightBoundary = parseTimeToHours(s.nightStartTime || '19:00');
                              const dayEnd = endH < nightBoundary ? endH : nightBoundary;
                              if (dayEnd > startH) dayH = dayEnd - startH;
                              const nStart = startH > nightBoundary ? startH : nightBoundary;
                              if (endH > nStart) nightH = endH - nStart;

                              const dayRate = s.pricePerHour || 0;
                              const nightRate = s.nightPricePerHour != null ? s.nightPricePerHour : dayRate;
                              return (dayH * dayRate) + (nightH * nightRate);
                            };

                            const slotPrice = getSlotPrice(session);

                            const effectivePrice = (effectiveLaneCount > 1 && session.lanePricePerHour > 0)
                              ? (session.lanePricePerHour * effectiveLaneCount)
                              : slotPrice;

                            return { ...session, effectiveLaneCount, effectivePrice };
                          });

                          const unpaidSessions = facilitySessions.filter(s => !s.isPaid);
                          const paidSessions = facilitySessions.filter(s => s.isPaid);
                          const displaySessions = activeTab === 'unpaid' ? unpaidSessions : paidSessions;

                          let facilityTotal = 0;
                          let facilityPaid = 0;
                          facilitySessions.forEach(session => {
                            facilityTotal += session.effectivePrice;
                            if (session.isPaid) facilityPaid += session.effectivePrice;
                          });
                          const facilityRemaining = facilityTotal - facilityPaid;
                          const allPaid = facilitySessions.length > 0 && unpaidSessions.length === 0;
                          grandTotal += facilityTotal;
                          grandPaid += facilityPaid;

                          return (
                            <div key={fIdx} style={{
                              borderRadius: '12px',
                              overflow: 'hidden',
                              border: allPaid ? '2px solid #86efac' : '2px solid #e3e6f0',
                              background: '#fff'
                            }}>
                              {/* Facility Header */}
                              <div style={{
                                background: allPaid
                                  ? 'linear-gradient(135deg, #1cc88a 0%, #17a673 100%)'
                                  : 'linear-gradient(135deg, #4e73df 0%, #3a5bc7 100%)',
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap'
                              }}>
                                <div>
                                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', display: 'block' }}>{facilityName}</span>
                                  {(() => {
                                    // Tesisin kendine ait tarihlerini kartsaltından (info.regionBreakdowns) bulalım
                                    const slot = slots[0];
                                    if (!slot) return null;
                                    const region = info.regionBreakdowns?.find(r => r.clubApplicationId === slot.clubApplicationId);
                                    if (region?.membershipStartDate && region?.membershipEndDate) {
                                      return (
                                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem', display: 'block', marginTop: '2px' }}>
                                          Üyelik: {new Date(region.membershipStartDate).toLocaleDateString('tr-TR')} - {new Date(region.membershipEndDate).toLocaleDateString('tr-TR')}
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                                <div style={{
                                  background: 'rgba(255,255,255,0.2)',
                                  borderRadius: '50px',
                                  padding: '3px 10px',
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  marginTop: '4px'
                                }}>
                                  {allPaid ? (
                                    <><FaCheckCircle size={11} /> Tamamlandı</>
                                  ) : (
                                    <><FaClock size={11} /> {unpaidSessions.length} Ödenmemiş Seans</>
                                  )}
                                </div>
                              </div>

                              {/* Tabs: Unpaid / Paid */}
                              <div style={{
                                display: 'flex',
                                borderBottom: '2px solid #f1f5f9',
                                background: '#fafbfc'
                              }}>
                                <button
                                  onClick={() => setPaymentFacilityTab(prev => ({ ...prev, [facilityName]: 'unpaid' }))}
                                  style={{
                                    flex: 1, padding: '8px 10px', border: 'none',
                                    background: activeTab === 'unpaid' ? '#fff' : 'transparent',
                                    borderBottom: activeTab === 'unpaid' ? '2px solid #e74a3b' : '2px solid transparent',
                                    color: activeTab === 'unpaid' ? '#e74a3b' : '#64748b',
                                    fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'
                                  }}
                                >
                                  <FaTimesCircle size={12} /> Ödenmemiş ({unpaidSessions.length})
                                </button>
                                <button
                                  onClick={() => setPaymentFacilityTab(prev => ({ ...prev, [facilityName]: 'paid' }))}
                                  style={{
                                    flex: 1, padding: '8px 10px', border: 'none',
                                    background: activeTab === 'paid' ? '#fff' : 'transparent',
                                    borderBottom: activeTab === 'paid' ? '2px solid #1cc88a' : '2px solid transparent',
                                    color: activeTab === 'paid' ? '#1cc88a' : '#64748b',
                                    fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'
                                  }}
                                >
                                  <FaCheckCircle size={12} /> Ödenmiş ({paidSessions.length})
                                </button>
                              </div>

                              {/* Session List */}
                              <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                {displaySessions.length === 0 ? (
                                  <div style={{ padding: '20px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                                    {activeTab === 'unpaid' ? 'Tüm seanslar ödenmiş durumda.' : 'Henüz ödenmiş seans bulunmuyor.'}
                                  </div>
                                ) : (
                                  displaySessions.map((session, sIdx) => (
                                    <div key={sIdx} style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      padding: '8px 16px',
                                      borderBottom: sIdx < displaySessions.length - 1 ? '1px solid #f1f5f9' : 'none',
                                      background: sIdx % 2 === 0 ? '#fff' : '#fafbfc'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{
                                          width: '6px', height: '6px', borderRadius: '50%',
                                          background: session.isPaid ? '#1cc88a' : '#e74a3b', flexShrink: 0
                                        }}></div>
                                        <div>
                                          <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.83rem' }}>
                                            {session.dateDisplay}
                                            <span style={{
                                              marginLeft: '6px', fontSize: '0.7rem', fontWeight: 700,
                                              color: session.isExtraSession ? '#7c3aed' : (session.isTransferTarget ? '#0284c7' : '#4e73df'), 
                                              background: session.isExtraSession ? '#f3e8ff' : (session.isTransferTarget ? '#e0f2fe' : '#eef0ff'),
                                              padding: '1px 6px', borderRadius: '4px'
                                            }}>
                                              {session.isExtraSession ? 'Ek Seans' : (session.isTransferTarget ? 'Hedef Seans' : getDayName(session.dayOfWeek))}
                                            </span>
                                          </div>
                                          <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '1px' }}>
                                            <FaClock size={9} style={{ marginRight: '3px', position: 'relative', top: '1px' }} />
                                            {session.startTime} - {session.endTime}
                                          </div>
                                        </div>
                                      </div>
                                      <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                          {(session.effectivePrice || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                        </div>
                                        {session.effectiveLaneCount > 1 && (
                                          <div style={{ fontSize: '0.68rem', color: '#0284c7', fontWeight: 600, marginTop: '1px' }}>
                                            {session.effectiveLaneCount} kulvar
                                          </div>
                                        )}
                                        {session.isPaid && !session.isTransferred && !session.isTransferTarget && !session.isExtraSession && (
                                          <button
                                            className="btn btn-sm"
                                            style={{ background: '#4e73df', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', fontSize: '0.7rem', marginTop: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}
                                            onClick={() => openTransferModal(session)}
                                            title="Bu seansı ileriye aktar"
                                          >
                                            <FaExchangeAlt /> Aktar
                                          </button>
                                        )}
                                        {session.isTransferred && (
                                          <div style={{ textAlign: 'right', marginTop: '4px' }}>
                                            <span style={{ fontSize: '0.7rem', color: '#f6c23e', fontWeight: 700, display: 'block' }}>
                                              <FaExchangeAlt style={{ marginRight: '3px' }}/> Aktarıldı
                                            </span>
                                            {session.transferTargetDate && (
                                              <div style={{ fontSize: '0.65rem', color: '#858796', fontStyle: 'italic' }}>
                                                {dayjs(session.transferTargetDate).format('DD MMMM YYYY')} {session.transferTargetStartTime}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>

                              {/* Facility Footer */}
                              <div style={{
                                padding: '10px 16px',
                                background: '#f8f9fc',
                                borderTop: '2px solid #f1f5f9',
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr',
                                gap: '6px',
                                textAlign: 'center',
                                fontSize: '0.78rem'
                              }}>
                                <div>
                                  <div style={{ color: '#94a3b8' }}>Toplam</div>
                                  <div style={{ fontWeight: 700, color: '#1e293b' }}>{facilityTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</div>
                                </div>
                                <div>
                                  <div style={{ color: '#94a3b8' }}>Ödenen</div>
                                  <div style={{ fontWeight: 700, color: '#1cc88a' }}>{facilityPaid.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</div>
                                </div>
                                <div>
                                  <div style={{ color: '#94a3b8' }}>Kalan</div>
                                  <div style={{ fontWeight: 700, color: '#e74a3b' }}>{facilityRemaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}


                      </div>
                    )}

                    {/* Ödeme Geçmişi */}
                    {paymentTransactions.length > 0 && (
                      <div style={{ marginTop: '1.5rem' }}>
                        <h4 style={{ margin: '0 0 0.75rem', color: '#5a5c69', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FaCheckCircle style={{ color: '#1cc88a' }} /> Ödeme Geçmişi ({paymentTransactions.length})
                        </h4>
                        <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e3e6f0' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                            <thead>
                              <tr style={{ background: 'linear-gradient(135deg, #1cc88a 0%, #17a673 100%)' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#fff', fontWeight: 700 }}>Tarih</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', color: '#fff', fontWeight: 700 }}>Tutar</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', color: '#fff', fontWeight: 700 }}>Ödeme Tipi</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', color: '#fff', fontWeight: 700 }}>Kulvar</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', color: '#fff', fontWeight: 700 }}>Kart</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', color: '#fff', fontWeight: 700 }}>Dekont</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentTransactions.map((tx, tIdx) => {
                                let displayLaneCount = tx.laneCount || 1;
                                return (
                                  <tr key={tx.id || tIdx} style={{ background: tIdx % 2 === 0 ? '#fff' : '#f8fdf9', borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500 }}>
                                      {new Date(tx.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </td>
                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1cc88a' }}>
                                      {(tx.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                    </td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                        <span style={{
                                          background: tx.paymentMode === 'full' ? '#e8f5e9' : tx.paymentMode === 'monthly' ? '#fff3e0' : tx.paymentMode === 'daily' ? '#e3f2fd' : '#f5f5f5',
                                          color: tx.paymentMode === 'full' ? '#2e7d32' : tx.paymentMode === 'monthly' ? '#e65100' : tx.paymentMode === 'daily' ? '#1565c0' : '#666',
                                          padding: '2px 8px',
                                          borderRadius: '4px',
                                          fontSize: '0.75rem',
                                          fontWeight: 700
                                        }}>
                                          {tx.paymentMode === 'full' ? 'Tam Ödeme' : tx.paymentMode === 'monthly' ? 'Aylık' : tx.paymentMode === 'daily' ? 'Günlük' : tx.paymentMode || 'Bilinmiyor'}
                                        </span>
                                        {tx.hasInactiveAppointments && (
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                            <span style={{
                                              background: '#ffe4e6',
                                              color: '#e11d48',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              fontSize: '0.65rem',
                                              fontWeight: 600,
                                              textAlign: 'center',
                                              maxWidth: '120px',
                                              lineHeight: '1.2'
                                            }}>
                                              Eski saat atamasına<br />ödeme yapıldı
                                            </span>
                                            {tx.inactiveAppointmentDetails && tx.inactiveAppointmentDetails.length > 0 && (
                                              <div style={{ fontSize: '0.65rem', color: '#e11d48', textAlign: 'center', fontWeight: '500', marginTop: '1px' }}>
                                                {tx.inactiveAppointmentDetails.map((det, i) => (
                                                  <div key={i}>{det}</div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                      {displayLaneCount > 1 || (displayLaneCount === 1 && paymentModalApp?.facilitiesData?.some(f => f.facilityName?.toLowerCase().includes('havuz') || f.sessions?.some(s => s.lanePricePerHour > 0))) ? (
                                        <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>
                                          {displayLaneCount} Kulvar
                                        </span>
                                      ) : (
                                        <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>—</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center', color: '#64748b', fontSize: '0.78rem' }}>
                                      {tx.cardMask || '—'}
                                    </td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setReceiptData({
                                              isClub: true,
                                              transactionId: tx.id || tx.transactionId,
                                              amount: tx.amount,
                                              createdAt: tx.createdAt,
                                              paymentMode: tx.paymentMode,
                                              firstName: paymentModalApp.firstName,
                                              lastName: paymentModalApp.lastName,
                                              taxNumber: paymentModalApp.taxNumber,
                                              clubName: `${paymentModalApp.firstName} ${paymentModalApp.lastName}`,
                                              message: 'Kulüp Ödemesi'
                                          });
                                          setTimeout(() => {
                                              window.print();
                                          }, 200);
                                        }}
                                        style={{
                                          background: '#fff', border: '1px solid #1cc88a', color: '#1cc88a', 
                                          borderRadius: '6px', padding: '4px 8px', cursor: 'pointer',
                                          fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'
                                        }}
                                      >
                                        <FaPrint /> Al
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <div style={{
                            padding: '8px 12px',
                            background: '#f0fdf4',
                            borderTop: '2px solid #86efac',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '0.85rem'
                          }}>
                            <span style={{ fontWeight: 700, color: '#166534' }}>Toplam Ödenen</span>
                            <span style={{ fontWeight: 800, color: '#166534', fontSize: '1rem' }}>
                              {paymentTransactions.reduce((sum, tx) => sum + (tx.amount || 0), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Manual Payment Section */}
            {userRole === 'admin' && (
              <div style={{ padding: '1rem', borderTop: '2px dashed #e3e6f0', background: '#f8f9fc' }}>
                
                {paymentModalData?.additionalDebts?.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h5 style={{ margin: '0 0 0.5rem', color: '#1e293b', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaMoneyBillWave color="#e74a3b" /> Yansıtılmış Ek Borçlar (Ödenmemiş)
                    </h5>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered" style={{ background: '#fff', fontSize: '0.85rem', marginBottom: 0 }}>
                        <thead style={{ background: '#eaecf4' }}>
                          <tr>
                            <th>Tutar</th>
                            <th>Açıklama</th>
                            <th>Tarih</th>
                            <th width="40" className="text-center">İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentModalData.additionalDebts.map(debt => (
                            <tr key={debt.id}>
                              <td style={{ fontWeight: 600, color: '#e74a3b' }}>{debt.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                              <td>{debt.description || '-'}</td>
                              <td>{new Date(debt.createdAt).toLocaleDateString('tr-TR')}</td>
                              <td className="text-center">
                                <button 
                                  onClick={() => handleRemoveClubDebt(debt.id)}
                                  style={{ background: 'none', border: 'none', color: '#e74a3b', cursor: 'pointer', outline: 'none' }}
                                  title="Borcu İptal Et/Sil"
                                >
                                  <FaTrashAlt />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!showManualPaymentForm ? (
                  <button
                    onClick={() => setShowManualPaymentForm(true)}
                    className="btn"
                    style={{ background: '#e74a3b', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '0.85rem' }}
                  >
                    <FaMoneyBillWave /> Ek Borç/Ceza Yansıt
                  </button>
                ) : (
                  <div style={{ background: '#fff', padding: '1rem', borderRadius: '8px', border: '1px solid #e3e6f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <h5 style={{ margin: '0 0 1rem', color: '#1e293b', fontSize: '0.9rem' }}>Ek Borçlandırma Bilgileri</h5>
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>Bu özellik, kulübün hesabına online olarak ödemesi gereken ekstra bir borç/ceza bedeli yansıtır.</p>
                    <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 2fr' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>Yansıtılacak Tutar (₺)</label>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          className="form-control"
                          value={manualPaymentAmount}
                          onChange={(e) => setManualPaymentAmount(e.target.value)}
                          placeholder="Örn: 600"
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>Not / Açıklama</label>
                        <input
                          type="text"
                          className="form-control"
                          value={manualPaymentNotes}
                          onChange={(e) => setManualPaymentNotes(e.target.value)}
                          placeholder="Örn: Eksik bakiye veya hasar bedeli"
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => {
                          setShowManualPaymentForm(false);
                          setManualPaymentAmount('');
                          setManualPaymentNotes('');
                        }}
                        style={{ background: '#e2e8f0', color: '#475569', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                      >
                        İptal
                      </button>
                      <button
                        onClick={handleSubmitManualPayment}
                        disabled={manualPaymentLoading}
                        style={{ background: '#1cc88a', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        {manualPaymentLoading ? 'Kaydediliyor...' : <><FaCheckCircle /> Kaydet</>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '1rem', borderTop: '1px solid #e3e6f0' }}>
              <button
                onClick={() => setShowPaymentModal(false)}
                style={{ background: '#858796', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer' }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {activeView === 'card-form' && <CardApplicationForm />}
      {activeView === 'card-load' && <CardBalanceLoad />}
      {activeView === 'card-applications' && <CardApplicationsList showToast={showToast} />}
      {activeView === 'women-training' && <WomenTrainingApplicationsList showToast={showToast} />}
      {activeView === 'balance-control' && <CardTransactionsList />}
      {activeView === 'payment-history' && <PaymentDashboard />}

      {activeView === 'how-to-guide' && (
        <div style={{ maxWidth: '900px', margin: '2rem auto', padding: '0 1rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, #f0f7fc, #e8f4fd)',
            borderRadius: '20px',
            padding: '2rem',
            marginBottom: '2rem',
            border: '1px solid #b3d4ea',
            textAlign: 'center'
          }}>
            <h2 style={{ color: '#0f75bc', fontSize: '1.8rem', fontWeight: '800', margin: '0 0 0.5rem' }}>Spor Paneli Kullanim Rehberi</h2>
            <p style={{ color: '#475569', fontSize: '1rem', margin: 0 }}>Paneldeki tüm işlemlerin adım adım açıklamaları</p>
          </div>

          {/* Step style helper */}
          {(() => {
            const stepBox = { background: '#f8fafc', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '0.5rem', borderLeft: '3px solid #0f75bc', fontSize: '0.95rem', color: '#1e293b', lineHeight: '1.6' };
            const stepNum = () => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', background: '#0f75bc', color: '#fff', fontSize: '0.75rem', fontWeight: '800', marginRight: '0.5rem', flexShrink: 0 });
            const sectionCard = { background: '#fff', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' };
            const sectionHeader = () => ({ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' });
            const badge = (bg) => ({ background: bg, color: '#fff', borderRadius: '10px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: '800', flexShrink: 0 });
            const highlight = { background: '#e0f2fe', padding: '2px 8px', borderRadius: '6px', fontWeight: '700', color: '#0369a1', fontSize: '0.9rem' };
            const tipBox = { ...stepBox, borderLeftColor: '#f59e0b', background: '#fffbeb' };
            const stepsContainer = { paddingLeft: '3.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' };

            return (<>
              {/* 1. Randevular */}
              <div style={sectionCard}>
                <div style={sectionHeader()}>
                  <span style={badge('#4e73df')}>1</span>
                  <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.15rem' }}>Randevuları Görüntüleme ve Aktarma</h3>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 1rem', paddingLeft: '3.25rem' }}>Panel açıldığında sol panelde bölge/saha seçimi, altta randevu listesi görünür.</p>
                <div style={stepsContainer}>
                  <div style={stepBox}><span style={stepNum()}>1</span> Randevuları filtrelemek için listenin üzerindeki <span style={highlight}>Tümü</span>, <span style={highlight}>Onayli</span> veya <span style={highlight}>Odeme Alinamadi</span> sekmelerine tıklayın. Her sekmenin yanında adet sayısı görünür.</div>
                  <div style={stepBox}><span style={stepNum()}>2</span> Belirli bir kişiyi bulmak için arama kutusuna <strong>isim</strong> veya <strong>TC kimlik no</strong> yazın.</div>
                  <div style={stepBox}><span style={stepNum()}>3</span> Bir randevuyu başka tarihe taşımak için satırdaki <span style={highlight}>Aktar</span> butonuna tıklayın.</div>
                  <div style={stepBox}><span style={stepNum()}>4</span> Açılan pencerede mevcut randevu bilgileri görünür. <strong>Aktarılacak Tarih</strong> bölümünden tarih seçin. <span style={highlight}>Onceki Gun</span> ve <span style={highlight}>Sonraki Gun</span> butonları veya takvim ile tarih değiştirebilirsiniz.</div>
                  <div style={stepBox}><span style={stepNum()}>5</span> Alttaki <strong>Müsait Saatler</strong> listesinden istediğiniz saate tıklayın ve onay diyalogunda <span style={highlight}>Onayla</span> butonuna basın.</div>
                  <div style={tipBox}><strong>İpucu:</strong> Mobil görünümde randevu kartlarına tıklayarak detay penceresi açılır, oradan da <span style={highlight}>Aktar</span> butonuna ulaşabilirsiniz.</div>
                </div>
              </div>

              {/* 2. Saatler / Fiyatlandırma */}
              <div style={sectionCard}>
                <div style={sectionHeader()}>
                  <span style={badge('#1cc88a')}>2</span>
                  <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.15rem' }}>Saat Dilimi ve Fiyat Ayarlama</h3>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 1rem', paddingLeft: '3.25rem' }}>Tesislere özel saat dilimleri ve fiyatlar tanımlarsınız.</p>
                <div style={stepsContainer}>
                  <div style={stepBox}><span style={stepNum()}>1</span> Ana sayfadaki <span style={highlight}>Saatler</span> butonuna tıklayın.</div>
                  <div style={stepBox}><span style={stepNum()}>2</span> <strong>Tesis Seçimi</strong> açılır menüsünden işlem yapılacak tesisi seçin. Tesisin standart ücreti ve çalışma saatleri bilgi kutusunda görünür.</div>
                  <div style={stepBox}><span style={stepNum()}>3</span> Belirli bir güne özel fiyat tanımlamak için <strong>Gün Seçimi</strong> menüsünden günü seçin. "Her Gün (Genel Kural)" seçili bırakırsanız tüm günler için geçerli olur.</div>
                  <div style={stepBox}><span style={stepNum()}>4</span> Yeni saat aralığı eklemek için <span style={highlight}>+ Yeni Saat Aralığı Ekle</span> butonuna tıklayın. Açılan pencerede <strong>Başlangıç</strong>, <strong>Bitiş</strong> saatlerini ve <strong>Fiyat</strong> alanını doldurup <span style={highlight}>Kaydet</span> butonuna basın.</div>
                  <div style={stepBox}><span style={stepNum()}>5</span> Mevcut bir saat dilimini düzenlemek için listeden <strong>saat kutusuna tıklayın</strong>. Açılan pencerede fiyatı değiştirip kaydedin veya <span style={highlight}>Sil</span> butonu ile kaldırın.</div>
                  <div style={stepBox}><span style={stepNum()}>6</span> <strong>Toplu tarife uygulamak için:</strong> <span style={highlight}>Tarife Uygula</span> butonuna tıklayın, ayarlı akşam başlangıç saatine göre <strong>Sabah</strong> ve <strong>Akşam</strong> fiyatlarını girin, <span style={highlight}>Tarifeyi Uygula</span> butonuna basın.</div>
                  <div style={tipBox}><strong>İpucu:</strong> Çok amaçlı tesislerde (basketbol/voleybol) <strong>Spor Türü</strong> menüsü otomatik olarak açılır, spor türüne göre ayrı fiyat belirleyebilirsiniz.</div>
                </div>
              </div>

              {/* 3. Takvim */}
              <div style={sectionCard}>
                <div style={sectionHeader()}>
                  <span style={badge('#e74a3b')}>3</span>
                  <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.15rem' }}>Takvimde Saat Bloklama (Kapatma)</h3>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 1rem', paddingLeft: '3.25rem' }}>Bakım veya özel durumlar için saatleri geçici olarak kapatabilirsiniz.</p>
                <div style={stepsContainer}>
                  <div style={stepBox}><span style={stepNum()}>1</span> Ana sayfadaki <span style={highlight}>Takvim</span> butonuna tıklayın.</div>
                  <div style={stepBox}><span style={stepNum()}>2</span> Üstteki açılır menüden işlem yapılacak <strong>tesisi seçin</strong>. Haftalık takvim görünecektir.</div>
                  <div style={stepBox}><span style={stepNum()}>3</span> Kapatmak istediğiniz <strong style={{ color: '#22543d' }}>yeşil (Uygun)</strong> saat kutusuna tıklayın. Bloklama penceresi açılır.</div>
                  <div style={stepBox}><span style={stepNum()}>4</span> Açılan pencerede <strong>Kapatma Tipi</strong> seçin: <span style={highlight}>Bakım</span>, <span style={highlight}>Üye Saati</span> veya <span style={highlight}>Bakanlık Saati</span>.</div>
                  <div style={stepBox}><span style={stepNum()}>5</span> İsterseniz <strong>Sebep</strong> alanına açıklama yazın (örn: "Turnuva"). Bu yazı kullanıcıya gösterilir.</div>
                  <div style={stepBox}><span style={stepNum()}>6</span> Her hafta tekrarlanmasını istiyorsanız <span style={highlight}>Her hafta bu gün ve saatte tekrarlansın</span> kutusunu işaretleyin.</div>
                  <div style={stepBox}><span style={stepNum()}>7</span> <span style={highlight}>Engelle</span> butonuna tıklayarak kapatmayı kaydedin.</div>
                  <div style={stepBox}><span style={stepNum()}>8</span> Bir bloklamayı kaldırmak için <strong style={{ color: '#9b2c2c' }}>kırmızı (Bloklu)</strong> saat kutusuna tıklayın ve onay kutusunda <span style={highlight}>Tamam</span> butonuna basın.</div>
                  <div style={tipBox}><strong>İpucu:</strong> Haftalar arasında gezmek için <span style={highlight}>Onceki</span> ve <span style={highlight}>Sonraki</span> butonlarını kullanın. <span style={highlight}>Mevcut Hafta</span> butonu sizi bugünün haftasına döndürür.</div>
                </div>
              </div>

              {/* 4. Kulüp Başvuruları */}
              <div style={sectionCard}>
                <div style={sectionHeader()}>
                  <span style={badge('#6f42c1')}>4</span>
                  <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.15rem' }}>Kulüp Başvuru Yönetimi ve Saat Atama</h3>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 1rem', paddingLeft: '3.25rem' }}>Kulüp başvurularını onaylayıp, tesislere saat ataması yaparsınız.</p>
                <div style={stepsContainer}>
                  <div style={stepBox}><span style={stepNum()}>1</span> Ana sayfadaki <span style={highlight}>Kulüp Bilgisi</span> butonuna tıklayın. Tüm başvurular kartlar halinde listelenir.</div>
                  <div style={stepBox}><span style={stepNum()}>2</span> Her kartta kulüp adı, tesis, bölge, TC, e-posta, telefon ve sorumluluk günleri bilgisi yer alır.</div>

                  <div style={{ ...stepBox, borderLeftColor: '#f6c23e', background: '#fffdf5' }}><strong>Bekleyen Başvurular İçin:</strong></div>
                  <div style={stepBox}><span style={stepNum()}>3</span> Onaylamak için <span style={highlight}>Onayla</span> butonuna tıklayın, açılan pencerede <strong>Üyelik Süresi</strong> (<span style={highlight}>1 Ay</span>, <span style={highlight}>3 Ay</span> veya <span style={highlight}>6 Ay</span>) seçin ve <span style={highlight}>Onayla (X Ay)</span> butonuna basın.</div>
                  <div style={stepBox}><span style={stepNum()}>4</span> Reddetmek için <span style={highlight}>Reddet</span> butonuna tıklayın.</div>

                  <div style={{ ...stepBox, borderLeftColor: '#4e73df', background: '#f0f4ff' }}><strong>Onaylanmış Kulüplere Saat Atama:</strong></div>
                  <div style={stepBox}><span style={stepNum()}>5</span> Onaylanmış kulübün kartındaki <span style={highlight}>Saat Ata</span> butonuna tıklayın.</div>
                  <div style={stepBox}><span style={stepNum()}>6</span> Açılan pencerede, eğer daha önce atanmış saatler varsa <strong>Mevcut Atamalar</strong> tablosunda görünür (tesis, gün, saat, ödeme durumu).</div>
                  <div style={stepBox}><span style={stepNum()}>7</span> Yeni atama yapmak için alt formdaki <strong>Gün</strong> menüsünden gün seçin, <strong>Başlangıç</strong> ve <strong>Bitiş</strong> saatini girin ve <span style={highlight}>Saat Ata</span> butonuna basın.</div>
                  <div style={stepBox}><span style={stepNum()}>8</span> Mevcut bir atamayı silmek için tabloda o satırdaki <span style={highlight}>Kaldır</span> butonuna tıklayın.</div>

                  <div style={{ ...stepBox, borderLeftColor: '#e74a3b', background: '#fdf2f2' }}><strong>Süresi Dolmuş Kulüpler:</strong></div>
                  <div style={stepBox}><span style={stepNum()}>9</span> Üyelik süresi dolmuş kulüpler kırmızı "SÜRESİ DOLMUŞ" etiketi gösterir. <span style={highlight}>Süreyi Yenile</span> butonu ile üyeliği uzatabilirsiniz.</div>
                  <div style={tipBox}><strong>İpucu:</strong> Üyelik süresi dolmuş kulüplere saat ataması yapılamaz, önce süreyi yenilemeniz gerekir.</div>
                </div>
              </div>

              {/* 5. Tenis Akademisi */}
              {hasRaufAccess && (
                <div style={sectionCard}>
                  <div style={sectionHeader()}>
                    <span style={badge('#7c3aed')}>5</span>
                    <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.15rem' }}>Tenis Akademisi Başvuruları</h3>
                  </div>
                  <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 1rem', paddingLeft: '3.25rem' }}>Tenis akademisine gelen başvuruları yönetirsiniz.</p>
                  <div style={stepsContainer}>
                    <div style={stepBox}><span style={stepNum()}>1</span> Ana sayfadaki <span style={highlight}>Tenis Akademisi</span> butonuna tıklayın.</div>
                    <div style={stepBox}><span style={stepNum()}>2</span> Gelen başvuruların listesi görüntülenir. Başvuru sahibinin bilgilerini kontrol edin.</div>
                    <div style={stepBox}><span style={stepNum()}>3</span> Başvuruyu kabul etmek için <span style={highlight}>Onayla</span>, reddetmek için <span style={highlight}>Reddet</span> butonuna tıklayın.</div>
                  </div>
                </div>
              )}

              {/* 6. Kart Başvuruları */}
              {canViewCardManagement && (
                <div style={sectionCard}>
                  <div style={sectionHeader()}>
                    <span style={badge('#2c9faf')}>6</span>
                    <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.15rem' }}>Kart Başvurularını Yönetme</h3>
                  </div>
                  <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 1rem', paddingLeft: '3.25rem' }}>Spor kartı almak isteyen vatandaşların başvurularını değerlendirirsiniz.</p>
                  <div style={stepsContainer}>
                    <div style={stepBox}><span style={stepNum()}>1</span> Ana sayfadaki <span style={highlight}>Kart Başvuruları</span> butonuna tıklayın.</div>
                    <div style={stepBox}><span style={stepNum()}>2</span> Başvuru listesinde kişi bilgilerini (ad, soyad, TC) kontrol edin.</div>
                    <div style={stepBox}><span style={stepNum()}>3</span> Başvuruyu onaylamak için <span style={highlight}>Onayla</span>, reddetmek için <span style={highlight}>Reddet</span> butonuna tıklayın.</div>
                  </div>
                </div>
              )}

              {/* 7. Bakiye Kontrol */}
              {canViewCardManagement && (
                <div style={sectionCard}>
                  <div style={sectionHeader()}>
                    <span style={badge('#858796')}>7</span>
                    <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.15rem' }}>Bakiye İşlemlerini Kontrol Etme</h3>
                  </div>
                  <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 1rem', paddingLeft: '3.25rem' }}>Kart bakiye yükleme ve harcama işlemlerini takip edersiniz.</p>
                  <div style={stepsContainer}>
                    <div style={stepBox}><span style={stepNum()}>1</span> Ana sayfadaki <span style={highlight}>Bakiye Kontrol</span> butonuna tıklayın.</div>
                    <div style={stepBox}><span style={stepNum()}>2</span> Tüm bakiye yükleme ve harcama işlemleri tarih sırasıyla listelenir.</div>
                    <div style={stepBox}><span style={stepNum()}>3</span> Belirli bir işlemi bulmak için <strong>tarih aralığı</strong> veya <strong>kart numarası</strong> ile filtreleyin.</div>
                  </div>
                </div>
              )}

              {/* Genel İpuçları */}
              <div style={{ background: 'linear-gradient(135deg, #fefce8, #fef9c3)', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.25rem', border: '1px solid #fde68a', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <h3 style={{ margin: '0 0 1rem', color: '#92400e', fontSize: '1.15rem' }}>Genel İpuçları</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ background: '#fff', borderRadius: '8px', padding: '0.6rem 1rem', border: '1px solid #fde68a', color: '#78350f', fontSize: '0.95rem' }}>Başka bir bölüme geçtikten sonra ana sayfaya dönmek için üstteki <span style={highlight}>Randevulara Dön</span> butonuna tıklayın.</div>
                  <div style={{ background: '#fff', borderRadius: '8px', padding: '0.6rem 1rem', border: '1px solid #fde68a', color: '#78350f', fontSize: '0.95rem' }}>Yaptığınız değişiklikler (saat ekleme, bloklama, onaylama) anında kaydedilir.</div>
                  <div style={{ background: '#fff', borderRadius: '8px', padding: '0.6rem 1rem', border: '1px solid #fde68a', color: '#78350f', fontSize: '0.95rem' }}>Vatandaşlar public siteden randevu aldığında, randevular otomatik olarak bu panele düşer.</div>
                  <div style={{ background: '#fff', borderRadius: '8px', padding: '0.6rem 1rem', border: '1px solid #fde68a', color: '#78350f', fontSize: '0.95rem' }}>Kulüp saat atamasında çakışma varsa sistem uyarı verir, çakışan saat atanamaz.</div>
                  <div style={{ background: '#fff', borderRadius: '8px', padding: '0.6rem 1rem', border: '1px solid #fde68a', color: '#78350f', fontSize: '0.95rem' }}>Sorun yaşamanız durumunda sistem yöneticisiyle iletişime geçin.</div>
                </div>
              </div>
            </>);
          })()}
        </div>
      )}

      {/* Custom Confirm Dialog */}
      {confirmDialog && (
        <div className="confirm-dialog-overlay" onClick={confirmDialog.onCancel}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-dialog-icon">↻</div>
            <div className="confirm-dialog-message">{confirmDialog.message}</div>
            <div className="confirm-dialog-actions">
              <button className="confirm-btn cancel" onClick={confirmDialog.onCancel}>İptal</button>
              <button className="confirm-btn confirm" onClick={confirmDialog.onConfirm}>Onayla</button>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <div className={`custom-toast ${toast.type}`}>
          <div className="toast-icon">
            {toast.type === 'success' ? '✓' : '✗'}
          </div>
          <div className="toast-content">
            {toast.message.split('\n').map((line, i) => (
              <div key={i} className={i === 0 ? 'toast-title' : 'toast-detail'}>{line}</div>
            ))}
          </div>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {receiptData && <ReceiptPrint data={receiptData} isClub={receiptData.isClub} />}

      {/* Transfer Modal */}
      {clubTransferModalVisible && (
        <div className="appointment-modal-overlay" onClick={() => setClubTransferModalVisible(false)} style={{ zIndex: 9999 }}>
          <div className="appointment-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ background: '#4e73df', color: 'white' }}>
              <h3>Seans Aktarma</h3>
              <button className="modal-close" onClick={() => setClubTransferModalVisible(false)} style={{ color: 'white' }}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
              {clubTransferData && (
                <div style={{ background: '#f8f9fc', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                  <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Aktarılacak Seans:</p>
                  <p style={{ margin: '0 0 4px', fontSize: '0.9rem' }}>Gün: <strong>{getDayName(clubTransferData.dayOfWeek)}</strong></p>
                  <p style={{ margin: '0 0 4px', fontSize: '0.9rem' }}>Saat: <strong>{clubTransferData.startTime} - {clubTransferData.endTime}</strong></p>
                  <p style={{ margin: 0, fontSize: '0.9rem' }}>Tarih: <strong>{new Date(clubTransferData.sessionDate).toLocaleDateString('tr-TR')}</strong></p>
                </div>
              )}
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>Hedef Tarih (*):</label>
                <DatePicker 
                  value={clubTransferDate ? dayjs(clubTransferDate) : null}
                  onChange={(date) => setClubTransferDate(date ? date.toDate() : null)}
                  format="DD.MM.YYYY"
                  style={{ width: '100%', height: '40px' }}
                  placeholder="Yeni tarih seçin"
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  popupStyle={{ zIndex: 10000 }}
                />
              </div>

              {clubTransferDate && (
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>Müsait Saat Seçimi (*):</label>
                  {clubAvailableTransferSlotsLoading ? (
                    <div style={{ padding: '10px', background: '#f8f9fc', color: '#64748b', fontSize: '0.85rem', borderRadius: '6px', textAlign: 'center' }}>
                      Müsait saatler yükleniyor...
                    </div>
                  ) : clubAvailableTransferSlots.length === 0 ? (
                    <div style={{ padding: '10px', background: '#fff1f0', color: '#cf1322', fontSize: '0.85rem', borderRadius: '6px', border: '1px solid #ffa39e' }}>
                      Seçilen tarihte tesiste uygun saat bulunamadı. Lütfen başka bir tarih seçin.
                    </div>
                  ) : (
                    <select
                      className="form-control"
                      style={{ width: '100%', height: '40px', border: '1px solid #d9d9d9', borderRadius: '6px', padding: '4px 11px' }}
                      value={clubTransferStartTime && clubTransferEndTime ? `${clubTransferStartTime} - ${clubTransferEndTime}` : ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) {
                          const [start, end] = val.split(' - ');
                          setClubTransferStartTime(start);
                          setClubTransferEndTime(end);
                        } else {
                          setClubTransferStartTime('');
                          setClubTransferEndTime('');
                        }
                      }}
                    >
                      <option value="">-- Çevrimiçi Saat Seçin --</option>
                      {clubAvailableTransferSlots.map((slot, sIdx) => {
                        const isDisabled = slot.status === 'Full' || slot.status === 'Blocked';
                        let label = slot.time;
                        
                        if (slot.status === 'Full') label += ' (DOLU)';
                        else if (slot.status === 'Blocked') label += ` (${slot.blockReason || 'Blokeli'})`;
                        else if (slot.isCapacitySlot) label += ' (Kapasite Müsait)';

                        return (
                          <option key={sIdx} value={slot.time} disabled={isDisabled} style={{ color: isDisabled ? '#94a3b8' : 'inherit' }}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              )}

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>Aktarım Notu (İsteğe bağlı):</label>
                <textarea className="form-control" rows="2" style={{ width: '100%', border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px 11px', resize: 'vertical' }} value={clubTransferNotes} onChange={(e) => setClubTransferNotes(e.target.value)} placeholder="Örn: Kulüp turnuvaya katıldığı için tarih değiştirildi..."></textarea>
              </div>
            </div>
            
            <div className="modal-actions" style={{ background: '#f8f9fc', padding: '15px 20px', borderTop: '1px solid #e3e6f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="mgmt-btn" style={{ background: 'white', color: '#5a5c69', border: '1px solid #d1d3e2' }} onClick={() => setClubTransferModalVisible(false)}>
                İptal
              </button>
              <button className="mgmt-btn" style={{ background: '#4e73df', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={handleTransfer} disabled={clubTransferLoading || !clubTransferDate || !clubTransferStartTime || !clubTransferEndTime}>
                {clubTransferLoading ? 'Aktarılıyor...' : <><FaExchangeAlt /> Aktarımı Onayla</>}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SportsAppointments;


